import { useState, useEffect, useContext, useCallback } from "react";
import {
  Settings,
  Clock,
  Calendar,
  AlertCircle,
  CheckCircle,
  Save,
  RotateCcw,
  TrendingUp,
  Award,
  Users,
  Building,
  FileText,
  Shield,
  Bell,
  Search,
  Plus,
  X,
  DollarSign,
} from "lucide-react";
import { ThemeContext } from "./theme";
import { LanguageContext } from "./LanguageContext";
import { apiFetch } from "../context/AuthContext";
import { useProfile } from "../hooks/useProfile";
import ReusableDropdown from "../components/ReusableDropdown";
import AlertModal from "../components/AlertModal";

const CompanyConfiguration = () => {
  const { theme } = useContext(ThemeContext);
  const { language } = useContext(LanguageContext);
  const { profile } = useProfile();
  const BASE_URL = process.env.REACT_APP_BASE_URL;

  const [loading, setLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState("company");
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [detectedCompanies, setDetectedCompanies] = useState([]);
  const [detectingCompanies, setDetectingCompanies] = useState(false);
  const [selectedCompanyName, setSelectedCompanyName] = useState("");
  const [departments, setDepartments] = useState([]);
  const [workingCompanies, setWorkingCompanies] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);

  // ===== PERMISSIONS STATE =====
  const [isAdmin, setIsAdmin] = useState(false); // Track if user is super admin
  const [modulePermissions, setModulePermissions] = useState({
    view: true,
    edit: true,
    detectCompanies: true,
    reset: true,
    // Tab-specific permissions
    viewCompanyInfo: true,
    editCompanyInfo: true,
    viewAttendance: true,
    editAttendance: true,
    viewOvertime: true,
    editOvertime: true,
    viewLeaves: true,
    editLeaves: true,
    viewAdvancements: true,
    editAdvancements: true,
  });
  const [loadingPermissions, setLoadingPermissions] = useState(true);

  // Archived configurations UI state
  const [archivedConfigs, setArchivedConfigs] = useState([]);
  const [archivesLoading, setArchivesLoading] = useState(false);
  const [selectedArchiveId, setSelectedArchiveId] = useState(null);
  const [isEditingArchive, setIsEditingArchive] = useState(false);
  const [isCreatingNewConfig, setIsCreatingNewConfig] = useState(false);

  // Archive helpers (component scope)
  const loadArchives = async (companyName = null) => {
    if (!companyName) companyName = selectedCompanyName;
    if (!companyName) return;
    try {
      setArchivesLoading(true);
      const clientName = profile?.clientName || localStorage.getItem("clientName");
      const url = `${BASE_URL}/api/company-config/${clientName}/${encodeURIComponent(
        companyName
      )}/archive/history?limit=100`;
      const resp = await apiFetch(url, { method: "GET" });
      if (resp.ok) {
        const d = await resp.json();
        if (d.success && d.data) {
          setArchivedConfigs(d.data || []);
        }
      }
    } catch (err) {
      console.error("Failed to load archives:", err);
    } finally {
      setArchivesLoading(false);
    }
  };

  const selectArchive = (archive) => {
    if (!archive) return;
    setIsCreatingNewConfig(false);
    setSelectedArchiveId(archive.id);
    setIsEditingArchive(true);
    setConfig((prev) => ({ ...prev, ...archive }));
    // Navigate to attendance tab where the archive picker lives
    setActiveTab("attendance");
  };

  const updateArchive = async (archiveId) => {
    if (!archiveId) {
      showAlert('Warning', 'No archive selected', 'warning');
      return;
    }
    try {
      setLoading(true);
      const clientName = profile?.clientName || localStorage.getItem("clientName");
      const companyName = selectedCompanyName;

      const {
        companyName: _cn,
        industry,
        email,
        phone,
        address,
        website,
        department,
        departmentRules,
        ...editableConfig
      } = config;

      let payload;

      if (department) {
        // ── Department selected ──────────────────────────────────────────────────
        // The user is editing the archived rules for a specific department.
        // Changes are stored in config.departmentRules[department] by handleAttendanceChange.
        // We only patch attendanceRules[department] on the archive record — we must
        // NOT change any flat company-wide fields and must NOT call the live
        // /departments/:dept endpoint (that would corrupt the live config).
        const existingAttendanceRules =
          editableConfig.attendanceRules && typeof editableConfig.attendanceRules === 'object'
            ? editableConfig.attendanceRules
            : {};
        const updatedDeptRules = (departmentRules && departmentRules[department]) || {};

        payload = {
          clientName,
          companyName,
          attendanceRules: {
            ...existingAttendanceRules,
            [department]: updatedDeptRules,
          },
        };
      } else {
        // ── No department selected: update flat company-wide archive fields ───────
        const advancementConfig = prepareAdvancementConfigForSave(editableConfig);
        const {
          advancementEnabled: _ae,
          maxAdvancementAmount: _maa,
          maxAdvancementPercentOfSalary: _maps,
          minMonthsOfService: _mmos,
          maxActiveAdvancements: _maa2,
          defaultInstallments: _di,
          minInstallments: _mini,
          maxInstallments: _maxi,
          requireManagerApproval: _rma,
          requireHRApproval: _rha,
          autoApproveUnderAmount: _aaua,
          advancementProcessingDays: _apd,
          allowEmergencyAdvancements: _aea,
          emergencyMaxAmount: _ema,
          deductionStartDelay: _dsd,
          blockAdvancementIfPending: _baip,
          blockAdvancementIfOverdue: _baio,
          advancementNotifications: _an,
          ...otherConfig
        } = editableConfig;

        payload = {
          clientName,
          companyName,
          ...otherConfig,
          ...advancementConfig,
        };
      }

      const url = `${BASE_URL}/api/company-config/${clientName}/${encodeURIComponent(
        companyName
      )}/archive/${archiveId}`;

      const resp = await apiFetch(url, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await resp.json();
      if (!resp.ok || !data.success) throw new Error(data.message || 'Failed to update archive');

      showAlert('Success', 'Archive updated successfully', 'success');
      await loadArchives(companyName);
      setIsEditingArchive(false);
      setSelectedArchiveId(null);
      await loadConfiguration(companyName);
    } catch (err) {
      console.error('Error updating archive:', err);
      showAlert('Error', err.message || 'Failed to update archive', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ===== ALERT MODAL STATE =====
  const [alertModal, setAlertModal] = useState({
    open: false,
    title: '',
    message: '',
    variant: 'info'
  });
  const showAlert = (title, message, variant = 'info') => {
    setAlertModal({ open: true, title, message, variant });
  };
  const closeAlert = () => {
    setAlertModal(prev => ({ ...prev, open: false }));
  };

  // ===== FETCH PERMISSIONS =====
useEffect(() => {
  const fetchPermissions = async () => {
    const fullAdminPermissions = {
      view: true,
      edit: true,
      detectCompanies: true,
      reset: true,
      viewCompanyInfo: true,
      editCompanyInfo: true,
      viewAttendance: true,
      editAttendance: true,
      viewOvertime: true,
      editOvertime: true,
      viewLeaves: true,
      editLeaves: true,
      viewAdvancements: true,
      editAdvancements: true,
    };

    // Check if user is admin first via profile
    if (profile?.role === 'admin' || profile?.role === 'superadmin') {
      console.log('🔐 Admin user detected via profile - bypassing all permission checks');
      setIsAdmin(true);
      setModulePermissions(fullAdminPermissions);
      setLoadingPermissions(false);
      return;
    }

    // Fallback: check via token
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.role === 'admin' || payload.isAdmin === true) {
          console.log('🔐 Admin user detected via token - bypassing all permission checks');
          setIsAdmin(true);
          setModulePermissions(fullAdminPermissions);
          setLoadingPermissions(false);
          return;
        }
      } catch (e) {
        console.log('Could not parse token for admin check, falling back to API');
      }
    }

    try {
      setLoadingPermissions(true);
      const workingCompanyFromStorage = profile?.workingCompany || profile?.clientName || localStorage.getItem("workingCompany") || localStorage.getItem("clientName") || "";

      if (!workingCompanyFromStorage) {
        console.warn("No workingCompany found — using default permissions");
        setModulePermissions({
          view: true,
          edit: true,
          detectCompanies: true,
          reset: true,
        });
        setLoadingPermissions(false);
        return;
      }

      const response = await apiFetch(
        `${BASE_URL}/api/admin-levels/resolve/permissions?workingCompany=${encodeURIComponent(workingCompanyFromStorage)}`,
        {
          method: 'GET',
          headers: { "Content-Type": "application/json" },
        }
      );

      if (response.ok) {
        const json = await response.json();
        const permissions = json.data ?? json.permissions ?? json;

        console.log("🔍 [CompanyConfiguration] Raw permissions received:", permissions);

        // ✅ Check admin from API response as fallback
        if (
          permissions.role === 'admin' ||
          permissions.isAdmin === true ||
          permissions.isSuperAdmin === true ||
          permissions.allModulesAllowed === true
        ) {
          console.log('🔐 Admin user detected via API - granting full access');
          setIsAdmin(true);
          setModulePermissions(fullAdminPermissions);
          setLoadingPermissions(false);
          return;
        }

        // Non-admin: normalize permissions normally
        const normalize = (obj) => {
          const defaults = {
            view: true,
            edit: true,
            detectCompanies: true,
            reset: true,
          };

          if (!obj) return defaults;

          const modules = Array.isArray(obj.modules) ? obj.modules : [];
          const features = obj.features || {};

          const moduleCandidates = [
            "configurations", "configuration", "companyConfiguration",
            "company-configuration", "company_configuration",
            "CompanyConfiguration", "settings",
          ];

          let moduleKey = moduleCandidates.find((k) => modules.includes(k));
          if (!moduleKey && features && typeof features === "object") {
            moduleKey = Object.keys(features).find((k) => moduleCandidates.includes(k));
          }

          let viewEnabled = true;
          let editEnabled = false;
          let detectEnabled = false;
          let resetEnabled = false;
          let viewCompanyInfo = true;
          let editCompanyInfo = false;
          let viewAttendance = true;
          let editAttendance = false;
          let viewOvertime = true;
          let editOvertime = false;
          let viewLeaves = true;
          let editLeaves = false;
          let viewAdvancements = true;
          let editAdvancements = false;

          if (moduleKey) {
            const entry = features[moduleKey];
            if (Array.isArray(entry)) {
              viewEnabled = entry.includes("view") || entry.includes("read") || entry.includes("viewConfigurations");
              editEnabled = entry.some(f => f.includes("edit") || f.includes("update") || f.includes("save") || f.startsWith("edit"));
              detectEnabled = entry.includes("detectCompanies") || entry.includes("selectCompany");
              resetEnabled = entry.includes("reset");
              viewCompanyInfo = viewEnabled;
              editCompanyInfo = entry.includes("editCompanyInfo");
              viewAttendance = viewEnabled;
              editAttendance = entry.some(f => f === "editWorkSchedule" || f === "editLateRules" || f === "editAbsenceRules" || f === "editGracePeriods");
              viewOvertime = viewEnabled;
              editOvertime = entry.some(f => f === "enableOvertime" || f === "editOvertimeRate" || f === "editOvertimeLimits" || f === "editOvertimeApproval");
              viewLeaves = viewEnabled;
              editLeaves = entry.some(f => f === "editAnnualLeaveProgression" || f === "editSickLeaveQuota" || f === "editCasualLeaveQuota" || f === "editLeaveApprovalRules" || f === "editLeaveCarryover" || f === "leaveRules");
              viewAdvancements = viewEnabled;
              editAdvancements = entry.some(f => f === "enableAdvancements" || f === "editAdvancementLimits" || f === "editAdvancementPercentage" || f === "editAdvancementApproval" || f === "editRepaymentTerms" || f === "advancementRules");
            } else if (entry && typeof entry === "object") {
              viewEnabled = Boolean(entry.view || entry.read || entry.viewConfigurations);
              editEnabled = Boolean(entry.edit || entry.update || entry.save || Object.keys(entry).some(k => k.startsWith("edit")));
              detectEnabled = Boolean(entry.detectCompanies || entry.selectCompany);
              resetEnabled = Boolean(entry.reset);
              viewCompanyInfo = viewEnabled;
              editCompanyInfo = Boolean(entry.editCompanyInfo);
              viewAttendance = viewEnabled;
              editAttendance = Boolean(entry.editWorkSchedule || entry.editLateRules || entry.editAbsenceRules || entry.editGracePeriods);
              viewOvertime = viewEnabled;
              editOvertime = Boolean(entry.enableOvertime || entry.editOvertimeRate || entry.editOvertimeLimits || entry.editOvertimeApproval);
              viewLeaves = viewEnabled;
              editLeaves = Boolean(entry.editAnnualLeaveProgression || entry.editSickLeaveQuota || entry.editCasualLeaveQuota || entry.editLeaveApprovalRules || entry.editLeaveCarryover || entry.leaveRules);
              viewAdvancements = viewEnabled;
              editAdvancements = Boolean(entry.enableAdvancements || entry.editAdvancementLimits || entry.editAdvancementPercentage || entry.editAdvancementApproval || entry.editRepaymentTerms || entry.advancementRules);
            }
          } else {
            viewEnabled = modules.length > 0;
          }

          return {
            view: viewEnabled,
            edit: editEnabled,
            detectCompanies: detectEnabled,
            reset: resetEnabled,
            viewCompanyInfo,
            editCompanyInfo,
            viewAttendance,
            editAttendance,
            viewOvertime,
            editOvertime,
            viewLeaves,
            editLeaves,
            viewAdvancements,
            editAdvancements,
          };
        };

        setModulePermissions(normalize(permissions));
      } else {
        console.warn("Failed to fetch permissions, using defaults");
        setModulePermissions({ view: true, edit: true, detectCompanies: true, reset: true });
      }
    } catch (error) {
      console.error("Error fetching permissions:", error);
      setModulePermissions({ view: true, edit: true, detectCompanies: true, reset: true });
    } finally {
      setLoadingPermissions(false);
    }
  };

  fetchPermissions();
}, [BASE_URL]);
  // ===== PERMISSION HELPER FUNCTIONS =====
  const canEditField = (section) => {
    // Admin users bypass all permission checks
    if (isAdmin) return true;

    // Archive editing mode — allow editing regardless of section permissions
    if (isEditingArchive) return true;

    switch (section) {
      case 'company':
        return modulePermissions.editCompanyInfo;
      case 'attendance':
        return modulePermissions.editAttendance;
      case 'overtime':
        return modulePermissions.editOvertime;
      case 'leaves':
        return modulePermissions.editLeaves;
      case 'advancements':
        return modulePermissions.editAdvancements;
      default:
        return modulePermissions.edit;
    }
  };

  const getFieldStyle = (section, baseStyle) => {
    // Admin users always get full editing capabilities
    if (isAdmin) return baseStyle;

    const isEditable = canEditField(section);
    return {
      ...baseStyle,
      opacity: isEditable ? 1 : 0.7,
      cursor: isEditable ? 'text' : 'not-allowed',
      background: isEditable
        ? baseStyle.background
        : theme === 'dark' ? 'rgba(51, 65, 85, 0.3)' : 'rgba(241, 245, 249, 0.5)',
    };
  };

  // Reusable input wrapper with permission check
  const PermissionInput = ({ section, type = "text", value, onChange, style, ...props }) => {
    const canEdit = canEditField(section);
    return (
      <input
        type={type}
        value={value}
        onChange={(e) => canEdit && onChange(e)}
        disabled={!canEdit}
        style={getFieldStyle(section, style)}
        {...props}
      />
    );
  };

  // Reusable checkbox wrapper with permission check
  const PermissionCheckbox = ({ section, checked, onChange, style, ...props }) => {
    const canEdit = canEditField(section);
    return (
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => canEdit && onChange(e)}
        disabled={!canEdit}
        style={{ ...style, cursor: canEdit ? 'pointer' : 'not-allowed', opacity: canEdit ? 1 : 0.6 }}
        {...props}
      />
    );
  };

  // ============================================================================
  // FIXED: State - Add departmentRules to track rules per department
  // ============================================================================
  const [config, setConfig] = useState({
    companyName: "",
    attendanceRules: {}, // NEW: Store rules for each department
    industry: "",
    email: "",
    phone: "",
    address: "",
    website: "",
    // Effective dates for different rule types
    attendanceEffectiveDate: new Date().toISOString().split('T')[0],
    advancementEffectiveDate: new Date().toISOString().split('T')[0],
    overtimeEffectiveDate: new Date().toISOString().split('T')[0],
    leaveEffectiveDate: new Date().toISOString().split('T')[0],
    // Company-wide defaults (used when no department selected)
    workStartTime: "09:00",
    workEndTime: "18:00",
    workingHoursPerDay: 8,
    workingDaysPerWeek: 5,
    weekendDays: ["Saturday", "Sunday"],
    overtimeEnabled: true,
    overtimeRate: 1.5,
    overtimeStartAfter: 8,
    maxOvertimeHoursPerDay: 4,
    overtimeApprovalRequired: false,
    absenceDeductionPerDay: 0,
    defaultDailyRatePercentage: 0,
    // Late Deduction Rules
    lateDeductionRules: [
      { minutesFrom: 0, minutesTo: 15, deductionPercentage: 0 },
      { minutesFrom: 15, minutesTo: 30, deductionPercentage: 25 },
      { minutesFrom: 30, minutesTo: 60, deductionPercentage: 50 },
      { minutesFrom: 60, minutesTo: null, deductionPercentage: 100 },
    ],
    // ... rest of your fields
    annualLeaveRules: [
      { yearsFrom: 0, yearsTo: 1, days: 15 },
      { yearsFrom: 1, yearsTo: 10, days: 21 },
      { yearsFrom: 10, yearsTo: null, days: 30 },
    ],
    sickLeavePerYear: 15,
    casualLeavePerYear: 7,
    leaveApprovalRequired: true,
    carryOverLeaves: false,
    performanceBonusEnabled: true,
    attendanceBonusEnabled: true,
    monthlyBonusAmount: 0,
    attendanceBonusCriteria: 100,
    lateNotification: true,
    absenceNotification: true,
    overtimeNotification: true,
    leaveNotification: true,
    privateMessaging: true,
    documentSharing: true,
    performanceReviews: true,
    salarySlips: true,
    advancementEnabled: true,
    maxAdvancementAmount: 10000,
    maxAdvancementPercentOfSalary: 50,
    minMonthsOfService: 3,
    maxActiveAdvancements: 1,
    defaultInstallments: 3,
    minInstallments: 1,
    maxInstallments: 12,
    requireManagerApproval: true,
    requireHRApproval: true,
    autoApproveUnderAmount: 0,
    advancementProcessingDays: 3,
    allowEmergencyAdvancements: true,
    emergencyMaxAmount: 5000,
    deductionStartDelay: 1,
    blockAdvancementIfPending: true,
    blockAdvancementIfOverdue: true,
    advancementNotifications: true,
    payrollStartDate: null,
    halfDayRequestThreshold: null, // Allow user to set custom value
  });

  // ============ الترجمات الكاملة - تم تحسينها ============
  const text = {
    en: {
      pageTitle: "Company Configuration",
      pageSubtitle:
        "Configure your company's attendance rules, overtime policies and system features",
      totalCompanies: "Total Companies",
      configuredCompanies: "Configured Companies",
      selectedCompanyLabel: "Selected Company",
      saveSuccess: "Configuration saved successfully!",
      saving: "Saving...",
      saveConfiguration: "Save Configuration",
      createConfiguration: "Create Config",
      creatingConfiguration: "Creating...",
      startCreateConfiguration: "Create New Config",
      confirmCreateConfiguration: "Create Config Now",
      cancelCreateConfiguration: "Cancel Create",
      effectiveDateBannerDefault: "Effective dates are locked. Use Create New Config to edit effective dates.",
      effectiveDateBannerCreate: "Create mode is active: effective dates are now editable.",
      resetToDefault: "Reset to Default",
      confirmReset:
        "Are you sure you want to reset all configurations to default?",
      detectCompanies: "Select Company",
      detectingCompanies: "Loading...",
      currentlyConfiguring: "Configuring:",
      noCompanySelected: "No company selected",

      companyModal: {
        title: "Select Company to Configure",
        subtitle: "Choose a company from the list below",
        employeeCount: "Employees",
        configured: "Configured",
        notConfigured: "Not Configured",
        noCompanies: "No companies detected for this client",
        autoCreate: "Auto-create Default Configurations",
        close: "Close",
        selectCompany: "Select this company",
      },

      tabs: {
        companyInfo: "Company Info",
        attendance: "Attendance",
        overtime: "Overtime",
        leaves: "Leaves",
        rewards: "Rewards",
        features: "Features",
        advancements: "Advancements", // Add this
        payrollDates: "Payroll Dates",
      },

      company: {
        title: "Company Information",
        companyName: "Company Name",
        industry: "Industry",
        email: "Email",
        phone: "Phone",
        address: "Address",
        website: "Website",
        notSet: "Not set",
      },

      attendance: {
        title: "Attendance & Working Hours",
        effectiveDate: "Effective Date",
        workStartTime: "Work Start Time",
        workEndTime: "Work End Time",
        workingHoursPerDay: "Working Hours Per Day",
        workingDaysPerWeek: "Working Days Per Week",
        lateAbsenceRules: "Late & Absence Rules",
        absenceDeduction: "Absence Deduction Per Day",
        maxLatePerMonth: "Max Late Per Month",
        gracePeriods: "Grace Periods Per Month",
        noDeduction: "No deduction",
        quarterDay: "0.25 day",
        halfDay: "0.5 day",
        fullDay: "1 day",
        oneAndQuarterDay: "1.25 days",
        oneAndHalfDay: "1.5 days",
        twoDays: "2 days",
        lateDeductionRules: "Late Deduction Rules (By Minutes)",
        fromMinute: "From Minute",
        toMinute: "To Minute (leave empty for unlimited)",
        deductionPercentage: "Deduction % of Daily Rate",
        unlimited: "Unlimited",
        addRule: "Add Rule",
        removeRule: "Remove Last Rule",
        lateThresholdMinutes: "Late Threshold Minutes",
        additionalMinutes: "Additional Minutes to Add",
        defaultDailyRatePercentage: "Default Deduction % of Daily Rate",
        defaultDailyRatePercentageDesc: "Deduction percentage of the employee's daily rate applied when they check in but forget to check out (e.g. 0.5 = deduct half the daily rate). Set to 0 to disable.",
        halfDayRequestThreshold: "Half Day Request Threshold (Hours)",
        halfDayRequestThresholdDesc: "Minimum shift time change (in hours/decimal) required for early check-in/out requests to be considered Half Day Leave requests. For example: if set to 3, a request for 3+ hours difference will be treated as a Half Day Leave.",
      },

      overtime: {
        title: "Overtime Configuration",
        effectiveDate: "Effective Date",
        enableOvertime: "Enable Overtime",
        overtimeRate: "Overtime Rate (Multiplier)",
        overtimeStartsAfter: "Overtime Starts After (Hours)",
        maxOvertimePerDay: "Max Overtime Hours Per Day",
        approvalRequired: "Overtime Approval Required",
        notificationSettings: "Notification Settings",
        lateNotifications: "Late Notifications",
        absenceNotifications: "Absence Notifications",
        overtimeNotifications: "Overtime Notifications",
        leaveNotifications: "Leave Notifications",
      },

      leaves: {
        title: "Leave & Vacation Policies",
        effectiveDate: "Effective Date",
        progressiveLeave: "Progressive Annual Leave (Based on Service Years)",
        fromYear: "From Year",
        toYear: "To Year (leave empty for unlimited)",
        unlimited: "Unlimited",
        annualLeaveDays: "Annual Leave Days",
        addRule: "Add Rule",
        removeRule: "Remove Last Rule",
        otherLeaveTypes: "Other Leave Types",
        sickLeavePerYear: "Sick Leave Per Year (Days)",
        casualLeavePerYear: "Casual Leave Per Year (Days)",
        leaveApprovalRequired: "Leave Approval Required",
        carryOverLeaves: "Carry Over Leaves to Next Year",
      },
      // In the English translations (text.en):
      advancements: {
        title: "Salary Advancements (Solfa)",
        effectiveDate: "Effective Date",
        enableAdvancements: "Enable Salary Advancements",
        minMonthsBeforeRequest: "Minimum Months Before Request",
        maxAdvancementPercentage: "Max Advancement (% of Salary)",
        maxAdvancementAmount: "Max Advancement Amount",
        maxActiveAdvancements: "Max Active Advancements",
        repaymentMonths: "Repayment Period (Months)",
        interestRate: "Interest Rate (%)",
        requireApproval: "Require Manager Approval",
        allowMultipleRequests: "Allow Multiple Active Requests",
      },

      rewards: {
        title: "Rewards & Bonuses",
        performanceBonusEnabled: "Performance Bonus Enabled",
        attendanceBonusEnabled: "Attendance Bonus Enabled",
        monthlyBonusAmount: "Monthly Bonus Amount",
        attendanceBonusCriteria: "Attendance Bonus Criteria (%)",
      },

      features: {
        title: "System Features & Privacy",
        privateMessaging: "Private Messaging",
        documentSharing: "Document Sharing",
        performanceReviews: "Performance Reviews",
        salarySlips: "Salary Slips",
      },
    },

    ar: {
      pageTitle: "إعدادات الشركة",
      pageSubtitle:
        "قم بتكوين قواعد الحضور وسياسات العمل الإضافي وميزات النظام الخاصة بشركتك",
      totalCompanies: "إجمالي الشركات",
      configuredCompanies: "الشركات المهيأة",
      selectedCompanyLabel: "الشركة المختارة",
      saveSuccess: "تم حفظ الإعدادات بنجاح!",
      saving: "جاري الحفظ...",
      saveConfiguration: "حفظ الإعدادات",
      createConfiguration: "إنشاء إعداد جديد",
      creatingConfiguration: "جاري الإنشاء...",
      startCreateConfiguration: "إنشاء إعداد جديد",
      confirmCreateConfiguration: "تأكيد إنشاء الإعداد",
      cancelCreateConfiguration: "إلغاء الإنشاء",
      effectiveDateBannerDefault: "تواريخ السريان مقفلة. استخدم إنشاء إعداد جديد لتعديلها.",
      effectiveDateBannerCreate: "وضع الإنشاء مفعل: تواريخ السريان قابلة للتعديل الآن.",
      resetToDefault: "إعادة التعيين للافتراضي",
      confirmReset:
        "هل أنت متأكد من إعادة تعيين جميع الإعدادات للقيم الافتراضية؟",
      detectCompanies: "اختر الشركة",
      detectingCompanies: "جاري التحميل...",
      currentlyConfiguring: "الشركة الحالية :",
      noCompanySelected: "لم يتم اختيار شركة",

      companyModal: {
        title: "اختر الشركة للتكوين",
        subtitle: "اختر شركة من القائمة أدناه",
        employeeCount: "الموظفون",
        configured: "تم التكوين",
        notConfigured: "لم يتم التكوين",
        noCompanies: "لم يتم اكتشاف شركات لهذا العميل",
        autoCreate: "إنشاء تلقائي للتكوينات الافتراضية",
        close: "إغلاق",
        selectCompany: "اختر هذه الشركة",
      },

      // Arabic:
      tabs: {
        companyInfo: "معلومات الشركة",
        attendance: "الحضور",
        overtime: "العمل الإضافي",
        leaves: "الإجازات",
        rewards: "المكافآت",
        features: "الميزات",
        advancements: "السلف", // Add this
        payrollDates: "تواريخ الراتب",
      },

      company: {
        title: "معلومات الشركة",
        companyName: "اسم الشركة",
        industry: "المجال",
        email: "البريد الإلكتروني",
        phone: "الهاتف",
        address: "العنوان",
        website: "الموقع الإلكتروني",
        notSet: "غير محدد",
      },

      attendance: {
        title: "الحضور وساعات العمل",
        effectiveDate: "تاريخ السريان",
        workStartTime: "وقت بدء العمل",
        workEndTime: "وقت انتهاء العمل",
        workingHoursPerDay: "ساعات العمل في اليوم",
        workingDaysPerWeek: "أيام العمل في الأسبوع",
        lateAbsenceRules: "قواعد التأخير والغياب",
        absenceDeduction: "خصم الغياب لكل يوم",
        maxLatePerMonth: "الحد الأقصى للتأخير في الشهر",
        gracePeriods: "فترات السماح في الشهر",
        noDeduction: "بدون خصم",
        quarterDay: "ربع يوم (0.25)",
        halfDay: "نصف يوم (0.5)",
        fullDay: "يوم كامل (1)",
        oneAndQuarterDay: "يوم وربع (1.25)",
        oneAndHalfDay: "يوم ونصف (1.5)",
        twoDays: "يومين (2)",
        lateDeductionRules: "قواعد خصم التأخير (بالدقائق)",
        fromMinute: "من الدقيقة",
        toMinute: "إلى الدقيقة (اترك فارغا لغير محدود)",
        deductionPercentage: "نسبة الخصم من الراتب اليومي (%)",
        unlimited: "غير محدود",
        addRule: "إضافة قاعدة",
        removeRule: "حذف آخر قاعدة",
        lateThresholdMinutes: "دقائق حد التأخير",
        additionalMinutes: "الدقائق الإضافية المراد إضافتها",
        defaultDailyRatePercentage: "نسبة الخصم الافتراضية من الراتب اليومي",
        defaultDailyRatePercentageDesc: "نسبة الخصم من الراتب اليومي للموظف عند تسجيل الحضور بدون تسجيل الخروج (مثال: 0.5 = خصم نصف اليومية). اضبطها على 0 لتعطيل الخصم.",
        halfDayRequestThreshold: "حد طلب نصف اليوم (بالساعات)",
        halfDayRequestThresholdDesc: "الحد الأدنى من التغيير في وقت العمل (بالساعات/الكسور العشرية) المطلوب لاعتبار طلبات الرحيل المبكر أو الحضور المبكر طلبات نصف يوم إجازة. على سبيل المثال: إذا تم تعيينه على 3، فسيتم التعامل مع طلب بفرق 3+ ساعات كطلب نصف يوم إجازة.",
      },

      overtime: {
        title: "إعدادات العمل الإضافي",
        effectiveDate: "تاريخ السريان",
        enableOvertime: "تفعيل العمل الإضافي",
        overtimeRate: "معدل العمل الإضافي (المضاعف)",
        overtimeStartsAfter: "يبدأ العمل الإضافي بعد (ساعات)",
        maxOvertimePerDay: "الحد الأقصى لساعات العمل الإضافي في اليوم",
        approvalRequired: "يتطلب موافقة على العمل الإضافي",
        notificationSettings: "إعدادات الإشعارات",
        lateNotifications: "إشعارات التأخير",
        absenceNotifications: "إشعارات الغياب",
        overtimeNotifications: "إشعارات العمل الإضافي",
        leaveNotifications: "إشعارات الإجازات",
      },
      // In the Arabic translations (text.ar):
      advancements: {
        title: "السلف المالية (الصولفة)",
        effectiveDate: "تاريخ السريان",
        enableAdvancements: "تفعيل السلف المالية",
        minMonthsBeforeRequest: "الحد الأدنى للأشهر قبل الطلب",
        maxAdvancementPercentage: "الحد الأقصى للسلفة (% من الراتب)",
        maxAdvancementAmount: "الحد الأقصى لمبلغ السلفة",
        maxActiveAdvancements: "عدد السلف النشطة المسموح بها",
        repaymentMonths: "فترة السداد (بالأشهر)",
        interestRate: "معدل الفائدة (%)",
        requireApproval: "يتطلب موافقة المدير",
        allowMultipleRequests: "السماح بعدة طلبات نشطة",
      },

      leaves: {
        title: "سياسات الإجازات والعطلات",
        effectiveDate: "تاريخ السريان",
        progressiveLeave: "الإجازة السنوية التدريجية (حسب سنوات الخدمة)",
        fromYear: "من سنة",
        toYear: "إلى سنة (اتركه فارغاً لغير محدود)",
        unlimited: "غير محدود",
        annualLeaveDays: "أيام الإجازة السنوية",
        addRule: " إضافة قاعدة",
        removeRule: " حذف آخر قاعدة",
        otherLeaveTypes: "أنواع الإجازات الأخرى",
        sickLeavePerYear: "الإجازة المرضية في السنة (أيام)",
        casualLeavePerYear: "الإجازة العارضة في السنة (أيام)",
        leaveApprovalRequired: "يتطلب موافقة على الإجازة",
        carryOverLeaves: "نقل الإجازات المتبقية للسنة القادمة",
      },

      rewards: {
        title: "المكافآت والحوافز",
        performanceBonusEnabled: "تفعيل مكافأة الأداء",
        attendanceBonusEnabled: "تفعيل مكافأة الحضور",
        monthlyBonusAmount: "المكافأة الشهرية",
        attendanceBonusCriteria: "معايير مكافأة الحضور (%)",
      },

      features: {
        title: "ميزات النظام والخصوصية",
        privateMessaging: "الرسائل الخاصة",
        documentSharing: "مشاركة المستندات",
        performanceReviews: "تقييمات الأداء",
        salarySlips: "قسائم الراتب",
      },
    },
  };

  const t = text[language] || text.en;
  const isRTL = language === "ar";

  // ============ الدوال الرئيسية ============
  // ============================================================================
  // FIXED: Advancement Configuration Component
  // This fixes the data type mismatches and ensures proper database integration
  // ============================================================================

  // Add this helper function at the top of your CompanyConfiguration component
  const prepareAdvancementConfigForSave = (config) => {
    return {
      // Convert all advancement fields to proper types matching Prisma schema
      advancementEnabled: Boolean(config.advancementEnabled ?? true),
      maxAdvancementAmount: config.maxAdvancementAmount
        ? parseFloat(config.maxAdvancementAmount)
        : null,
      maxAdvancementPercentOfSalary: config.maxAdvancementPercentOfSalary
        ? parseFloat(config.maxAdvancementPercentOfSalary)
        : null,
      minMonthsOfService: config.minMonthsOfService
        ? parseInt(config.minMonthsOfService)
        : null,
      maxActiveAdvancements: config.maxActiveAdvancements
        ? parseInt(config.maxActiveAdvancements)
        : null,
      defaultInstallments: config.defaultInstallments
        ? parseInt(config.defaultInstallments)
        : null,
      minInstallments: config.minInstallments
        ? parseInt(config.minInstallments)
        : null,
      maxInstallments: config.maxInstallments
        ? parseInt(config.maxInstallments)
        : null,
      requireManagerApproval: Boolean(config.requireManagerApproval ?? true),
      requireHRApproval: Boolean(config.requireHRApproval ?? true),
      autoApproveUnderAmount: config.autoApproveUnderAmount
        ? parseFloat(config.autoApproveUnderAmount)
        : null,
      advancementProcessingDays: config.advancementProcessingDays
        ? parseInt(config.advancementProcessingDays)
        : null,
      allowEmergencyAdvancements: Boolean(
        config.allowEmergencyAdvancements ?? true
      ),
      emergencyMaxAmount: config.emergencyMaxAmount
        ? parseFloat(config.emergencyMaxAmount)
        : null,
      deductionStartDelay: config.deductionStartDelay
        ? parseInt(config.deductionStartDelay)
        : null,
      blockAdvancementIfPending: Boolean(
        config.blockAdvancementIfPending ?? true
      ),
      blockAdvancementIfOverdue: Boolean(
        config.blockAdvancementIfOverdue ?? true
      ),
      advancementNotifications: Boolean(
        config.advancementNotifications ?? true
      ),
    };
  };

  // ============================================================================
  // REPLACE YOUR loadConfiguration FUNCTION WITH THIS FIXED VERSION
  // ============================================================================
  const loadConfiguration = useCallback(
    async (companyName = null) => {
      try {
        setLoading(true);
        const clientName = profile?.clientName || localStorage.getItem("clientName");

        if (!companyName) {
          const detectResponse = await apiFetch(
            `${BASE_URL}/api/company-config/${clientName}/detect-companies`,
            {
              method: 'GET',
              headers: {
                "Content-Type": "application/json",
              },
            }
          );
          if (detectResponse.ok) {
            const detectData = await detectResponse.json();
            if (
              detectData.success &&
              detectData.data.companies &&
              detectData.data.companies.length > 0
            ) {
              const companies = (detectData.data.companies || []).map(
                (company) => ({
                  ...company,
                  isConfigured:
                    company.hasConfiguration || company.isConfigured || false,
                })
              );
              setDetectedCompanies(companies);
              companyName = detectData.data.companies[0].companyName;
              setSelectedCompanyName(companyName);
            }
          }
        }

        if (!companyName) {
          console.log("No company name available");
          setLoading(false);
          return;
        }

        const response = await apiFetch(
          `${BASE_URL}/api/company-config/${clientName}/${encodeURIComponent(
            companyName
          )}`,
          {
            method: 'GET',
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            // ============================================================================
            // FIX: Properly load advancement fields with null-safe defaults
            // ============================================================================
            const loadedConfig = {
              ...data.data,
              weekendDays: Array.isArray(data.data.weekendDays)
                ? data.data.weekendDays
                : data.data.weekendDays || ["Saturday", "Sunday"],
              annualLeaveRules: Array.isArray(data.data.annualLeaveRules)
                ? data.data.annualLeaveRules
                : data.data.annualLeaveRules || [
                  { yearsFrom: 0, yearsTo: 1, days: 15 },
                  { yearsFrom: 1, yearsTo: 10, days: 21 },
                  { yearsFrom: 10, yearsTo: null, days: 30 },
                ],
              // ✅ NEW: Initialize late deduction rules with safe defaults
              lateDeductionRules: Array.isArray(data.data.lateDeductionRules)
                ? data.data.lateDeductionRules
                : data.data.lateDeductionRules || [
                  { minutesFrom: 0, minutesTo: 15, deductionPercentage: 0 },
                  { minutesFrom: 15, minutesTo: 30, deductionPercentage: 25 },
                  { minutesFrom: 30, minutesTo: 60, deductionPercentage: 50 },
                  { minutesFrom: 60, minutesTo: null, deductionPercentage: 100 },
                ],

              // ✅ FIXED: Handle null values properly
              advancementEnabled: data.data.advancementEnabled ?? true,
              maxAdvancementAmount: data.data.maxAdvancementAmount ?? 10000,
              maxAdvancementPercentOfSalary:
                data.data.maxAdvancementPercentOfSalary ?? 50,
              minMonthsOfService: data.data.minMonthsOfService ?? 3,
              maxActiveAdvancements: data.data.maxActiveAdvancements ?? 1,
              defaultInstallments: data.data.defaultInstallments ?? 3,
              minInstallments: data.data.minInstallments ?? 1,
              maxInstallments: data.data.maxInstallments ?? 12,
              requireManagerApproval: data.data.requireManagerApproval ?? true,
              requireHRApproval: data.data.requireHRApproval ?? true,
              autoApproveUnderAmount: data.data.autoApproveUnderAmount ?? 0,
              advancementProcessingDays:
                data.data.advancementProcessingDays ?? 3,
              allowEmergencyAdvancements:
                data.data.allowEmergencyAdvancements ?? true,
              emergencyMaxAmount: data.data.emergencyMaxAmount ?? 5000,
              deductionStartDelay: data.data.deductionStartDelay ?? 1,
              blockAdvancementIfPending:
                data.data.blockAdvancementIfPending ?? true,
              blockAdvancementIfOverdue:
                data.data.blockAdvancementIfOverdue ?? true,
              advancementNotifications:
                data.data.advancementNotifications ?? true,
              halfDayRequestThreshold: data.data.halfDayRequestThreshold ?? null,
              department: data.data.department || "",
              workingCompany: data.data.workingCompany || "",
              // Load effective dates from backend
              attendanceEffectiveDate: data.data.attendanceEffectiveDate
                ? new Date(data.data.attendanceEffectiveDate).toISOString().split('T')[0]
                : new Date().toISOString().split('T')[0],
              overtimeEffectiveDate: data.data.overtimeEffectiveDate
                ? new Date(data.data.overtimeEffectiveDate).toISOString().split('T')[0]
                : new Date().toISOString().split('T')[0],
              leaveEffectiveDate: data.data.leaveEffectiveDate
                ? new Date(data.data.leaveEffectiveDate).toISOString().split('T')[0]
                : new Date().toISOString().split('T')[0],
              advancementEffectiveDate: data.data.advancementEffectiveDate
                ? new Date(data.data.advancementEffectiveDate).toISOString().split('T')[0]
                : new Date().toISOString().split('T')[0],
              payrollStartDate: data.data.payrollStartDate ?? null,
            };

            console.log("✅ Loaded advancement config:", {
              enabled: loadedConfig.advancementEnabled,
              maxAmount: loadedConfig.maxAdvancementAmount,
              maxPercent: loadedConfig.maxAdvancementPercentOfSalary,
            });

            setConfig((prevConfig) => ({ ...prevConfig, ...loadedConfig }));
            setSelectedCompanyName(companyName);
            setIsCreatingNewConfig(false);
          }
        }
      } catch (error) {
        console.error("Error loading configuration:", error);
      } finally {
        setLoading(false);
      }
    },
    [BASE_URL]
  );

  const handleDetectCompanies = async (autoCreate = false) => {
    try {
      setDetectingCompanies(true);
      const clientName = profile?.clientName || localStorage.getItem("clientName") || "Infinity";
      const url = `${BASE_URL}/api/company-config/${clientName}/detect-companies${autoCreate ? "?autoCreate=true" : ""
        }`;

      const response = await apiFetch(url, {
        method: 'GET',
        headers: {
          "Content-Type": "application/json",
        },
      });
      const data = await response.json();

      if (data.success) {
        const companies = (data.data.companies || []).map((company) => ({
          ...company,
          isConfigured:
            company.hasConfiguration || company.isConfigured || false,
        }));

        console.log("✅ Detected companies:", companies);
        console.log("🔒 Department restrictions:", data.data.departmentRestrictions);

        setDetectedCompanies(companies);

        // Store department restrictions globally for later use
        if (data.data.departmentRestrictions) {
          window.departmentRestrictions = data.data.departmentRestrictions;
        }

        setShowCompanyModal(true);

        if (autoCreate && data.data.created && data.data.created.length > 0) {
          showAlert(
            isRTL ? "نجاح" : "Success",
            isRTL ? `تم إنشاء ${data.data.created.length} إعدادات بنجاح!` : `Successfully created ${data.data.created.length} configurations!`,
            'success'
          );
          handleDetectCompanies(false);
        }
      } else {
        showAlert(isRTL ? "خطأ" : "Error", isRTL ? "فشل في اكتشاف الشركات: " + data.message : "Failed to detect companies: " + data.message, 'error');
      }
    } catch (error) {
      console.error("Error detecting companies:", error);
      showAlert(isRTL ? "خطأ" : "Error", isRTL ? "فشل في اكتشاف الشركات" : "Failed to detect companies", 'error');
    } finally {
      setDetectingCompanies(false);
    }
  };
  const persistConfiguration = async ({ createNewVersion = false } = {}) => {
    if (!selectedCompanyName) {
      showAlert(
        isRTL ? "تنبيه" : "Warning",
        isRTL
          ? 'يرجى اختيار شركة أولاً باستخدام زر "اختر الشركة"'
          : 'Please select a company first using the "Select Company" button',
        'warning'
      );
      return;
    }

    try {
      // If we're editing an archive, update the archive record instead of the live config
      if (isEditingArchive && selectedArchiveId) {
        await updateArchive(selectedArchiveId);
        return;
      }
      setLoading(true);
      const clientName = profile?.clientName || localStorage.getItem("clientName");
      const token = localStorage.getItem("token");

      // ============================================================================
      // SAVE COMPANY-WIDE CONFIG FIRST
      // ============================================================================
      const {
        companyName,
        industry,
        email,
        phone,
        address,
        website,
        department,
        departmentRules,
        ...editableConfig
      } = config;

      const advancementConfig = prepareAdvancementConfigForSave(editableConfig);
      const {
        advancementEnabled: _ae,
        maxAdvancementAmount: _maa,
        maxAdvancementPercentOfSalary: _maps,
        minMonthsOfService: _mmos,
        maxActiveAdvancements: _maa2,
        defaultInstallments: _di,
        minInstallments: _mini,
        maxInstallments: _maxi,
        requireManagerApproval: _rma,
        requireHRApproval: _rha,
        autoApproveUnderAmount: _aaua,
        advancementProcessingDays: _apd,
        allowEmergencyAdvancements: _aea,
        emergencyMaxAmount: _ema,
        deductionStartDelay: _dsd,
        blockAdvancementIfPending: _baip,
        blockAdvancementIfOverdue: _baio,
        advancementNotifications: _an,
        ...otherConfig
      } = editableConfig;

      // Company-wide attendance fields — only include when NO department is selected
      const COMPANY_WIDE_ATTENDANCE_FIELDS = ['workStartTime', 'workEndTime', 'workingHoursPerDay', 'workingDaysPerWeek', 'weekendDays', 'halfDayRequestThreshold'];

      let companyPayload = { ...otherConfig };
      if (department) {
        // Department is selected — do NOT overwrite company-wide attendance columns
        COMPANY_WIDE_ATTENDANCE_FIELDS.forEach((f) => delete companyPayload[f]);
      }

      const payload = {
        clientName,
        companyName: selectedCompanyName,
        createNewVersion,
        archiveReason: createNewVersion ? "manual_create_version" : undefined,
        ...companyPayload,
        ...advancementConfig,
      };

      console.log("📤 Sending company config:", payload);

      const response = await apiFetch(`${BASE_URL}/api/company-config`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const responseData = await response.json();

      if (!response.ok || !responseData.success) {
        throw new Error(
          responseData.message || "Failed to save company config"
        );
      }

      // ============================================================================
      // SAVE DEPARTMENT-SPECIFIC RULES IF DEPARTMENT IS SELECTED
      // ============================================================================
      if (department) {
        console.log(`📤 Saving department rules for: ${department}`);

        // Build department rules from current form state (editableConfig has the
        // live UI values). Include ALL attendance fields the backend stores per-department.
        const deptRulesPayload = {
          workStartTime: editableConfig.workStartTime,
          workEndTime: editableConfig.workEndTime,
          workingHoursPerDay: editableConfig.workingHoursPerDay,
          workingDaysPerWeek: editableConfig.workingDaysPerWeek,
          weekendDays: editableConfig.weekendDays,
          lateDeductionRules: editableConfig.lateDeductionRules,
          absenceDeductionPerDay: editableConfig.absenceDeductionPerDay,
          overtimeEnabled: editableConfig.overtimeEnabled,
          overtimeRate: editableConfig.overtimeRate,
          overtimeStartAfter: editableConfig.overtimeStartAfter,
          maxOvertimeHoursPerDay: editableConfig.maxOvertimeHoursPerDay,
          maxLatePerMonth: editableConfig.maxLatePerMonth,
          gracePeriodsPerMonth: editableConfig.gracePeriodsPerMonth,
          halfDayRequestThreshold: editableConfig.halfDayRequestThreshold,
        };

        const deptResponse = await apiFetch(
          `${BASE_URL}/api/company-config/${clientName}/${encodeURIComponent(
            selectedCompanyName
          )}/departments/${encodeURIComponent(department)}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(deptRulesPayload),
          }
        );

        const deptData = await deptResponse.json();

        if (deptResponse.ok && deptData.success) {
          console.log(`✅ Department rules saved for: ${department}`);
          if (deptData.departmentRecalculation) {
            console.log(
              "📊 Department payroll recalculated:",
              deptData.departmentRecalculation
            );
          }
        } else {
          console.warn(
            `⚠️ Failed to save department rules: ${deptData.message}`
          );
        }
      }

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);

      if (createNewVersion) {
        showAlert(
          isRTL ? "نجاح" : "Success",
          isRTL
            ? "تم إنشاء إصدار إعدادات جديد وأرشفة الإصدار السابق"
            : "New configuration version created and previous version archived",
          "success"
        );
      }

      await loadConfiguration(selectedCompanyName);
      await loadArchives(selectedCompanyName);
    } catch (error) {
      console.error("Error saving configuration:", error);
      showAlert(
        isRTL ? "خطأ" : "Error",
        (isRTL ? "فشل حفظ الإعدادات: " : "Failed to save configuration: ") +
        error.message,
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  // ============================================================================
  // Edit current config only (no archive rollover)
  // ============================================================================
  const handleSave = async () => {
    await persistConfiguration({ createNewVersion: false });
  };

  // ============================================================================
  // Create new config version (archive old + replace current)
  // ============================================================================
  const handleCreateConfig = async () => {
    if (isEditingArchive) {
      showAlert(
        isRTL ? "تنبيه" : "Warning",
        isRTL ? "اخرج من وضع تعديل الأرشيف أولاً" : "Exit archive edit mode first",
        "warning"
      );
      return;
    }

    if (!isCreatingNewConfig) {
      setIsCreatingNewConfig(true);
      return;
    }

    await persistConfiguration({ createNewVersion: true });
    setIsCreatingNewConfig(false);
  };
  // ============================================================================
  // ENHANCED: fetchDepartmentsAndCompanies with backend visibility filtering
  // ============================================================================
  const fetchDepartmentsAndCompanies = async () => {
    if (!selectedCompanyName) return;

    try {
      setLoadingDepartments(true);
      const clientName = profile?.clientName || localStorage.getItem("clientName");
      const token = localStorage.getItem("token");

      console.log(
        `📋 Fetching departments for company: ${selectedCompanyName}`
      );

      // ============================================================================
      // OPTION 1: Use dedicated departments endpoint with backend filtering
      // ============================================================================
      try {
        const deptResponse = await apiFetch(
          `${BASE_URL}/api/company-config/${clientName}/${selectedCompanyName}/departments-list`,
          {
            method: 'GET',
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (deptResponse.ok) {
          const deptData = await deptResponse.json();

          if (deptData.success && !deptData.data.accessDenied) {
            const departments = deptData.data.departments || [];

            console.log(`✅ Fetched ${departments.length} departments from backend`);
            console.log(`🔒 Has visibility restrictions:`, deptData.data.hasVisibilityRestrictions);
            console.log(`📋 Departments:`, departments);

            setDepartments(departments);

            // Also fetch working companies from employees endpoint
            const empResponse = await apiFetch(
              `${BASE_URL}/employees/clients/${clientName}`,
              {
                method: 'GET',
                headers: {
                  "Content-Type": "application/json",
                },
              }
            );

            if (empResponse.ok) {
              const empData = await empResponse.json();
              const employees = (empData.employees || []).filter(
                (emp) => emp.workingCompany === selectedCompanyName
              );

              const uniqueWorkingCompanies = [
                ...new Set(
                  employees
                    .map((emp) => emp.workingCompany)
                    .filter((company) => company && company.trim() !== "")
                ),
              ].sort();

              setWorkingCompanies(uniqueWorkingCompanies);
            }

            // ✅ LOAD DEPARTMENT-SPECIFIC RULES
            await loadDepartmentRules(departments);

            setLoadingDepartments(false);
            return;
          } else if (deptData.data.accessDenied) {
            console.warn(`⚠️ Access denied to company: ${selectedCompanyName}`);
            setDepartments([]);
            setWorkingCompanies([]);
            setLoadingDepartments(false);
            showAlert(
              isRTL ? "تحذير" : "Warning",
              isRTL ? "ليس لديك صلاحيات للوصول إلى هذه الشركة" : "You don't have access to this company",
              'warning'
            );
            return;
          }
        }
      } catch (backendError) {
        console.warn("⚠️ Backend department endpoint failed, falling back to client-side filtering:", backendError);
      }

      // ============================================================================
      // OPTION 2: Fallback to client-side filtering (original method)
      // ============================================================================
      const response = await apiFetch(
        `${BASE_URL}/employees/clients/${clientName}`,
        {
          method: 'GET',
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        const allEmployees = data.employees || [];

        const employees = allEmployees.filter(
          (emp) => emp.workingCompany === selectedCompanyName
        );

        console.log(
          `✅ Fetched ${employees.length} employees for company ${selectedCompanyName}`
        );

        let uniqueDepartments = [
          ...new Set(
            employees
              .map((emp) => emp.employeeType)
              .filter((type) => type && type.trim() !== "")
          ),
        ].sort();

        // ============================================================================
        // APPLY DEPARTMENT VISIBILITY RESTRICTIONS (CLIENT-SIDE FALLBACK)
        // ============================================================================
        if (window.departmentRestrictions && window.departmentRestrictions[selectedCompanyName]) {
          const allowedDepts = window.departmentRestrictions[selectedCompanyName];

          if (allowedDepts !== "ALL") {
            console.log(`🔒 Applying department filter for ${selectedCompanyName}:`, allowedDepts);
            uniqueDepartments = uniqueDepartments.filter(dept => allowedDepts.includes(dept));
            console.log(`✓ Filtered departments:`, uniqueDepartments);
          } else {
            console.log(`✓ ALL departments allowed for ${selectedCompanyName}`);
          }
        }

        const uniqueWorkingCompanies = [
          ...new Set(
            employees
              .map((emp) => emp.workingCompany)
              .filter((company) => company && company.trim() !== "")
          ),
        ].sort();

        console.log("📋 Unique Departments:", uniqueDepartments);
        console.log("🏢 Unique Working Companies:", uniqueWorkingCompanies);

        setDepartments(uniqueDepartments);
        setWorkingCompanies(uniqueWorkingCompanies);

        // ✅ LOAD DEPARTMENT-SPECIFIC RULES
        await loadDepartmentRules(uniqueDepartments);
      } else {
        console.error("❌ Failed to fetch employees:", response.status);
      }
    } catch (error) {
      console.error("❌ Error fetching departments:", error);
      showAlert(
        isRTL ? "خطأ" : "Error",
        isRTL ? "فشل في جلب الأقسام" : "Failed to fetch departments",
        'error'
      );
    } finally {
      setLoadingDepartments(false);
    }
  };

  useEffect(() => {
    loadConfiguration();
  }, [loadConfiguration]);

  useEffect(() => {
    if (selectedCompanyName) {
      fetchDepartmentsAndCompanies();
    }
  }, [selectedCompanyName]);

  // Load archived configs whenever the selected company changes.
  // loadArchives is a plain function (not memoized) so deliberately omitted
  // from deps to avoid a re-render loop.
  useEffect(() => {
    if (selectedCompanyName) {
      loadArchives(selectedCompanyName); // eslint-disable-line react-hooks/exhaustive-deps
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyName]);

  const handleSelectCompany = async (companyName) => {
    setSelectedCompanyName(companyName);
    setShowCompanyModal(false);
    await loadConfiguration(companyName);
  };

  const handleReset = () => {
    if (window.confirm(t.confirmReset)) {
      loadConfiguration(selectedCompanyName);
    }
  };

  // ============================================================================
  // NEW: Load department-specific rules from backend
  // ============================================================================
  const loadDepartmentRules = async (departmentsList) => {
    if (!selectedCompanyName || departmentsList.length === 0) return;

    const clientName = profile?.clientName || localStorage.getItem("clientName");
    const departmentRulesMap = {};

    for (const dept of departmentsList) {
      try {
        const response = await apiFetch(
          `${BASE_URL}/api/company-config/${clientName}/${encodeURIComponent(
            selectedCompanyName
          )}/departments/${encodeURIComponent(dept)}`,
          {
            method: 'GET',
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data.rules) {
            departmentRulesMap[dept] = data.data.rules;
            console.log(`✅ Loaded rules for department: ${dept}`);
          }
        }
      } catch (error) {
        console.error(`❌ Error loading rules for ${dept}:`, error);
      }
    }

    setConfig((prev) => ({ ...prev, departmentRules: departmentRulesMap }));
  };

  // ============ الأنماط المحسّنة ============
  const styles = {
    container: {
      marginTop: -40,
    },

    header: {
      background:
        theme === "dark"
          ? "linear-gradient(135deg, rgba(30, 41, 59, 0.01) 0%, rgba(15, 23, 42, 0.01) 100%)"
          : "linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.01) 100%)",
      padding: "clamp(28px, 4.5vw, 44px)",
      borderRadius: "clamp(18px, 2.8vw, 24px)",
      marginBottom: "28px",
      // boxShadow: theme === 'dark'
      //   ? '0 16px 50px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.12)'
      //   : '0 16px 50px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.9)',
      // backdropFilter: 'blur(25px)',
      border:
        theme === "dark"
          ? "1px solid rgba(255,255,255,0.18)"
          : "1px solid rgba(0,0,0,0.08)",
      position: "relative",
      overflow: "hidden",
    },

    headerGradientOverlay: {
      position: "absolute",
      top: 0,
      [isRTL ? "right" : "left"]: 0,
      width: "45%",
      height: "100%",
      // background: theme === 'dark'
      //   ? 'radial-gradient(circle at top left, rgba(46, 124, 186, 0.25), transparent 70%)'
      //   : 'radial-gradient(circle at top left, rgba(46, 124, 186, 0.15), transparent 70%)',
      pointerEvents: "none",
    },

    headerContent: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexWrap: "wrap",
      gap: "20px",
      position: "relative",
      zIndex: 1,
    },

    headerLeft: {
      flex: 1,
      minWidth: "280px",
      display: "flex",
      flexDirection: "column",
      gap: "12px",
    },

    headerTop: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      width: "100%",
      gap: "20px",
      flexWrap: "wrap",
    },

    headerRight: {
      display: "flex",
      flexDirection: "row",
      gap: "14px",
      alignItems: "center",
      flexShrink: 0,
    },

    /* Stats row */
    statsGrid: {
      display: "flex",
      gap: "14px",
      marginTop: "18px",
      alignItems: "stretch",
      flexWrap: "wrap",
    },

    statCard: {
      flex: "1 1 180px",
      minWidth: "160px",
      background: theme === "dark" ? "rgba(255,255,255,0.04)" : "#ffffff",
      borderRadius: "12px",
      padding: "12px 16px",
      display: "flex",
      alignItems: "center",
      gap: "12px",
      border:
        theme === "dark"
          ? "1px solid rgba(255,255,255,0.06)"
          : "1px solid rgba(0,0,0,0.06)",
      boxShadow:
        theme === "dark"
          ? "0 6px 18px rgba(0,0,0,0.5)"
          : "0 6px 16px rgba(0,0,0,0.04)",
    },

    statIconWrapper: {
      width: "48px",
      height: "48px",
      borderRadius: "10px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "white",
      flexShrink: 0,
    },

    statValue: {
      fontSize: "20px",
      fontWeight: "900",
      color: theme === "dark" ? "#fff" : "#0f172a",
    },

    statLabel: {
      fontSize: "12px",
      color: theme === "dark" ? "rgba(255,255,255,0.75)" : "#6b7280",
      fontWeight: "700",
    },

    title: {
      fontSize: "clamp(26px, 4.5vw, 36px)",
      fontWeight: "900",
      margin: 0,
      display: "flex",
      alignItems: "center",
      gap: "clamp(12px, 1.8vw, 16px)",
      color: theme === "dark" ? "#ffffff" : "#0f172a",
      flexDirection: isRTL ? "row-reverse" : "row",
      letterSpacing: "-0.03em",
      textShadow:
        theme === "dark"
          ? "0 2px 8px rgba(0,0,0,0.3)"
          : "0 1px 3px rgba(0,0,0,0.1)",
    },

    subtitle: {
      fontSize: "clamp(14px, 2.2vw, 17px)",
      color: theme === "dark" ? "rgba(255,255,255,0.85)" : "rgba(15,23,42,0.8)",
      margin: 0,
      textAlign: isRTL ? "right" : "left",
      lineHeight: "1.7",
      fontWeight: "500",
      marginBottom: "20px",
    },

    companyBadge: {
      display: "inline-flex",
      alignItems: "center",
      gap: "10px",
      padding: "10px 18px",
      borderRadius: "10px",
      background:
        theme === "dark"
          ? "rgba(46, 124, 186, 0.2)"
          : "rgba(46, 124, 186, 0.12)",
      border:
        theme === "dark"
          ? "1px solid rgba(46, 124, 186, 0.4)"
          : "1px solid rgba(46, 124, 186, 0.25)",
      color: "#2e7cba",
      fontSize: "clamp(13px, 1.8vw, 15px)",
      fontWeight: "800",
      marginTop: "12px", // <-- احذف هذا السطر
      //boxShadow: "0 4px 12px rgba(46, 124, 186, 0.15)",
    },

    detectButton: {
      padding: "clamp(13px, 2.2vw, 16px) clamp(22px, 3.5vw, 32px)",
      borderRadius: "clamp(12px, 1.8vw, 14px)",
      border: "none",
      background: "linear-gradient(135deg, #2e7cba 0%, #2e7cba 100%)",
      color: "white",
      fontSize: "clamp(14px, 2vw, 16px)",
      fontWeight: "800",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "clamp(10px, 1.5vw, 12px)",
      transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
      //boxShadow: "0 10px 25px rgba(46, 124, 186, 0.35)",
      //letterSpacing: "0.6px",
      textTransform: "uppercase",
      whiteSpace: "nowrap",
      position: "relative",
      overflow: "hidden",
    },

    modalOverlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: "rgba(0,0,0,0.8)",
      backdropFilter: "blur(12px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 9999,
      padding: "24px",
      animation: "fadeIn 0.25s ease",
    },

    modal: {
      background:
        theme === "dark"
          ? "linear-gradient(135deg, rgba(30, 41, 59, 0.99) 0%, rgba(15, 23, 42, 0.99) 100%)"
          : "linear-gradient(135deg, rgba(255, 255, 255, 0.99) 0%, rgba(248, 250, 252, 0.99) 100%)",
      borderRadius: "clamp(18px, 2.8vw, 26px)",
      padding: "clamp(28px, 4.5vw, 44px)",
      maxWidth: "900px",
      width: "100%",
      maxHeight: "85vh",
      overflow: "auto",
      boxShadow: "0 30px 70px rgba(0,0,0,0.6)",
      border:
        theme === "dark"
          ? "1px solid rgba(255,255,255,0.18)"
          : "1px solid rgba(0,0,0,0.08)",
      animation: "slideUp 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)",
      position: "relative",
    },

    modalTitle: {
      fontSize: "clamp(22px, 3.8vw, 32px)",
      fontWeight: "900",
      color: theme === "dark" ? "#ffffff" : "#0f172a",
      marginBottom: "10px",
      letterSpacing: "-0.03em",
      display: "flex",
      alignItems: "center",
      gap: "14px",
      flexDirection: isRTL ? "row" : "row",
    },

    modalSubtitle: {
      fontSize: "clamp(14px, 2.2vw, 17px)",
      color:
        theme === "dark" ? "rgba(255,255,255,0.75)" : "rgba(15,23,42,0.75)",
      marginBottom: "32px",
      fontWeight: "500",
      textAlign: isRTL ? "right" : "left",
    },

    companyCard: (hasConfig) => ({
      background:
        theme === "dark"
          ? hasConfig
            ? "linear-gradient(135deg, rgba(16, 185, 129, 0.18), rgba(5, 150, 105, 0.12))"
            : "rgba(255,255,255,0.08)"
          : hasConfig
            ? "linear-gradient(135deg, rgba(16, 185, 129, 0.12), rgba(5, 150, 105, 0.06))"
            : "rgba(248, 250, 252, 0.9)",
      borderRadius: "clamp(14px, 2.2vw, 18px)",
      padding: "clamp(20px, 3.8vw, 28px)",
      marginBottom: "18px",
      border: hasConfig
        ? theme === "dark"
          ? "2px solid rgba(16, 185, 129, 0.45)"
          : "2px solid rgba(16, 185, 129, 0.35)"
        : theme === "dark"
          ? "1px solid rgba(255,255,255,0.15)"
          : "1px solid rgba(0,0,0,0.1)",
      transition: "all 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)",
      cursor: "pointer",
      position: "relative",
      overflow: "hidden",
    }),

    companyName: {
      fontSize: "clamp(18px, 3vw, 24px)",
      fontWeight: "900",
      color: theme === "dark" ? "#ffffff" : "#0f172a",
      marginBottom: "14px",
      display: "flex",
      alignItems: "center",
      gap: "14px",
      letterSpacing: "-0.02em",
      flexDirection: isRTL ? "row" : "row",
    },

    companyDetails: {
      display: "flex",
      gap: "18px",
      fontSize: "clamp(13px, 1.9vw, 15px)",
      color:
        theme === "dark" ? "rgba(255,255,255,0.75)" : "rgba(15,23,42,0.75)",
      flexWrap: "wrap",
      alignItems: "center",
      marginBottom: "18px",
      flexDirection: isRTL ? "row" : "row",
    },

    badge: (configured) => ({
      padding: "7px 16px",
      borderRadius: "9px",
      fontSize: "13px",
      fontWeight: "800",
      background: configured
        ? "linear-gradient(135deg, #10b981, #059669)"
        : theme === "dark"
          ? "rgba(255,255,255,0.12)"
          : "rgba(0,0,0,0.12)",
      color: configured
        ? "white"
        : theme === "dark"
          ? "rgba(255,255,255,0.85)"
          : "#4b5563",
      textTransform: "uppercase",
      letterSpacing: "0.6px",
      boxShadow: configured ? "0 5px 15px rgba(16, 185, 129, 0.25)" : "none",
    }),

    selectCompanyButton: {
      width: "100%",
      padding: "clamp(13px, 2.2vw, 16px)",
      borderRadius: "12px",
      border: "none",
      background: "linear-gradient(135deg, #2e7cba, #2e7cba)",
      color: "white",
      fontSize: "clamp(14px, 2vw, 16px)",
      fontWeight: "800",
      cursor: "pointer",
      transition: "all 0.3s ease",
      textTransform: "uppercase",
      letterSpacing: "0.6px",
      boxShadow: "0 5px 15px rgba(46, 124, 186, 0.35)",
    },

    autoCreateButton: {
      width: "100%",
      padding: "clamp(16px, 2.8vw, 20px)",
      borderRadius: "clamp(12px, 1.8vw, 14px)",
      border: "none",
      background: "linear-gradient(135deg, #2e7cba, #2e7cba)",
      color: "white",
      fontSize: "clamp(15px, 2.2vw, 17px)",
      fontWeight: "800",
      cursor: "pointer",
      transition: "all 0.3s ease",
      textTransform: "uppercase",
      letterSpacing: "0.6px",
      //boxShadow: "0 5px 15px rgba(245, 158, 11, 0.35)",
    },

    closeButton: {
      position: "absolute",
      top: "24px",
      [isRTL ? "left" : "right"]: "24px",
      background: "none",
      border: "none",
      color:
        theme === "dark" ? "rgba(255,255,255,0.75)" : "rgba(15,23,42,0.75)",
      cursor: "pointer",
      fontSize: "32px",
      padding: "0",
      width: "44px",
      height: "44px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "all 0.2s ease",
      borderRadius: "50%",
    },

    tabsContainer: {
      display: "flex",
      gap: "10px",
      marginBottom: "-25px",
      overflowX: "auto",
      paddingBottom: "14px",
      flexDirection: isRTL ? "row" : "row",
    },

    tab: (isActive) => ({
      padding: "clamp(13px, 2.2vw, 16px) clamp(18px, 2.8vw, 28px)",
      borderRadius: "clamp(12px, 1.8vw, 14px)",
      border: "none",
      background: isActive
        ? "linear-gradient(135deg, #2e7cba, #2e7cba)"
        : "transparent",
      color: isActive
        ? "white"
        : theme === "dark"
          ? "rgba(255,255,255,0.75)"
          : "rgba(15,23,42,0.75)",
      fontSize: "clamp(14px, 2vw, 15px)",
      fontWeight: isActive ? "800" : "700",
      cursor: "pointer",
      transition: "all 0.3s ease",
      whiteSpace: "nowrap",
      // boxShadow: isActive ? '0 5px 15px rgba(46, 124, 186, 0.35)' : 'none',
      textTransform: "uppercase",
      letterSpacing: "0.6px",
      display: "flex",
      alignItems: "center",
      gap: "10px",
      flexDirection: isRTL ? "row" : "row",
    }),

    contentSection: {
      background:
        theme === "dark" ? "rgba(255,255,255,0.0)" : "rgba(255,255,255,1)",
      borderRadius: "clamp(14px, 2.2vw, 18px)",
      padding: "clamp(26px, 4.2vw, 36px)",
      marginBottom: "28px",
      border:
        theme === "dark"
          ? "1px solid rgba(255,255,255,0.12)"
          : "1px solid rgba(0,0,0,0.08)",
      backdropFilter: "blur(15px)",
      boxShadow:
        theme === "dark"
          ? "0 8px 24px rgba(0,0,0,0.3)"
          : "0 4px 16px rgba(0,0,0,0.06)",
    },

    sectionTitle: {
      fontSize: "clamp(19px, 3.2vw, 26px)",
      fontWeight: "900",
      color: theme === "dark" ? "#ffffff" : "#0f172a",
      marginBottom: "24px",
      display: "flex",
      alignItems: "center",
      gap: "14px",
      letterSpacing: "-0.02em",
      flexDirection: isRTL ? "row" : "row",
    },

    formRow: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
      gap: "clamp(18px, 3.2vw, 26px)",
      marginBottom: "24px",
    },

    formGroup: {
      display: "flex",
      flexDirection: "column",
      gap: "10px",
    },

    label: {
      fontSize: "clamp(13px, 2vw, 15px)",
      fontWeight: "800",
      color: theme === "dark" ? "rgba(255,255,255,0.95)" : "#0f172a",
      textTransform: "uppercase",
      letterSpacing: "0.6px",
      display: "flex",
      alignItems: "center",
      gap: "10px",
      flexDirection: isRTL ? "row" : "row",
    },
    selectOption: {
      width: "100%",
      padding: "12px 14px",
      borderRadius: "12px",
      border:
        theme === 'dark' ? '1px solid rgba(148, 163, 184, 0.3)' : '1px solid rgba(148, 163, 184, 0.3)',
      background: theme === 'dark' ? 'rgba(51, 65, 85, 1.8)' : '#ffffff',
      color: theme === "dark" ? "#f1f5f9" : "#334155",
      fontSize: "14px",
      fontFamily: "inherit",
      cursor: "pointer",
      transition: "all 0.3s ease",
      direction: isRTL ? "rtl" : "ltr",
      boxSizing: "border-box",
    },

    // أضف style جديد للـ option
    selectOptionItem: {
      background:
        theme === "dark"
          ? "rgba(30, 41, 59, 0.98)"
          : "rgba(255, 255, 255, 0.98)",
      color: theme === "dark" ? "#ffffff" : "#0f172a",
      padding: "12px",
    },
    input: {
      width: "100%",
      padding: "12px 14px",
      borderRadius: "12px",
      background: theme === 'dark' ? 'rgba(51, 65, 85, 1.8)' : '#ffffff',
      border: theme === 'dark' ? '1px solid rgba(148, 163, 184, 0.3)' : '1px solid rgba(148, 163, 184, 0.3)',
      color: theme === "dark" ? "#f1f5f9" : "#334155",
      fontSize: "14px",
      fontFamily: "inherit",
      transition: "all 0.3s ease",
      boxSizing: "border-box",
    },

    select: {
      width: "100%",
      padding: "12px 14px",
      borderRadius: "12px",
      border:
        theme === 'dark' ? '1px solid rgba(148, 163, 184, 0.3)' : '1px solid rgba(148, 163, 184, 0.3)',
      background: theme === 'dark' ? 'rgba(51, 65, 85, 1.8)' : '#ffffff',
      color: theme === "dark" ? "#f1f5f9" : "#334155",
      fontSize: "14px",
      fontFamily: "inherit",
      cursor: "pointer",
      transition: "all 0.3s ease",
      direction: isRTL ? "rtl" : "ltr",
      boxSizing: "border-box",
    },

    checkbox: {
      width: "clamp(20px, 3.2vw, 22px)",
      height: "clamp(20px, 3.2vw, 22px)",
      cursor: "pointer",
      accentColor: "#2e7cba",
    },

    toggleContainer: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "clamp(16px, 2.8vw, 18px)",
      borderRadius: "12px",
      background:
        theme === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.03)",
      border:
        theme === "dark"
          ? "1px solid rgba(255,255,255,0.12)"
          : "1px solid rgba(0,0,0,0.08)",
      marginBottom: "14px",
      transition: "all 0.2s ease",
      flexDirection: isRTL ? "row" : "row",
    },

    actionButtons: {
      display: "flex",
      gap: "clamp(14px, 2.2vw, 18px)",
      marginTop: "36px",
      flexWrap: "wrap",
      justifyContent: isRTL ? "flex-start" : "flex-end",
      flexDirection: isRTL ? "row" : "row",
    },

    saveButton: {
      padding: "clamp(16px, 2.8vw, 18px) clamp(28px, 4.5vw, 36px)",
      borderRadius: "clamp(12px, 1.8vw, 14px)",
      border: "none",
      background: "linear-gradient(135deg, #2e7cba, #2e7cba)",
      color: "white",
      fontSize: "clamp(15px, 2.2vw, 16px)",
      fontWeight: "800",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "clamp(10px, 1.5vw, 12px)",
      transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
      // boxShadow: '0 10px 25px rgba(16, 185, 129, 0.35)',
      textTransform: "uppercase",
      letterSpacing: "0.6px",
      flexDirection: isRTL ? "row" : "row",
    },

    resetButton: {
      padding: "clamp(16px, 2.8vw, 18px) clamp(28px, 4.5vw, 36px)",
      borderRadius: "clamp(12px, 1.8vw, 14px)",
      border:
        theme === "dark"
          ? "2px solid rgba(255,255,255,0.25)"
          : "2px solid rgba(0,0,0,0.12)",
      background: "transparent",
      color: theme === "dark" ? "rgba(255,255,255,0.85)" : "#0f172a",
      fontSize: "clamp(15px, 2.2vw, 16px)",
      fontWeight: "800",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "clamp(10px, 1.5vw, 12px)",
      transition: "all 0.3s ease",
      textTransform: "uppercase",
      letterSpacing: "0.6px",
      flexDirection: isRTL ? "row" : "row",
    },

    successNotification: {
      position: "fixed",
      bottom: "32px",
      [isRTL ? "left" : "right"]: "32px",
      background: "linear-gradient(135deg, #10b981, #059669)",
      color: "white",
      padding: "clamp(18px, 2.8vw, 22px) clamp(24px, 3.5vw, 32px)",
      borderRadius: "clamp(14px, 2.2vw, 18px)",
      boxShadow: "0 15px 45px rgba(16, 185, 129, 0.4)",
      display: "flex",
      alignItems: "center",
      gap: "14px",
      fontSize: "clamp(15px, 2.2vw, 17px)",
      fontWeight: "800",
      zIndex: 10000,
      animation: "slideInUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
      border: "1px solid rgba(255,255,255,0.25)",
      flexDirection: isRTL ? "row" : "row",
    },

    leaveRuleContainer: {
      // background:
      //   theme === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.03)",
      borderRadius: "14px",
      padding: "clamp(18px, 2.8vw, 24px)",
      marginBottom: "18px",
      // border:
      //   theme === "dark"
      //     ? "1px solid rgba(255,255,255,0.12)"
      //     : "1px solid rgba(0,0,0,0.08)",
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
      gap: "clamp(14px, 2.2vw, 18px)",
    },

    addButton: {
      padding: "clamp(13px, 2.2vw, 16px) clamp(24px, 3.5vw, 32px)",
      borderRadius: "clamp(12px, 1.8vw, 14px)",
      border: "2px dashed rgba(46, 124, 186, 0.55)",
      background:
        theme === "dark"
          ? "rgba(46, 124, 186, 0.1)"
          : "rgba(46, 124, 186, 0.06)",
      color: "#2e7cba",
      fontSize: "clamp(14px, 2vw, 15px)",
      fontWeight: "800",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "10px",
      transition: "all 0.3s ease",
      textTransform: "uppercase",
      letterSpacing: "0.6px",
      flexDirection: isRTL ? "row" : "row",
    },

    removeButton: {
      padding: "clamp(11px, 2vw, 14px) clamp(20px, 2.8vw, 28px)",
      borderRadius: "10px",
      border: "none",
      background: "rgba(239, 68, 68, 0.18)",
      color: "#ef4444",
      fontSize: "clamp(13px, 1.9vw, 14px)",
      fontWeight: "800",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      transition: "all 0.25s ease",
      textTransform: "uppercase",
      letterSpacing: "0.4px",
      flexDirection: isRTL ? "row" : "row",
    },
  };
  // ============================================================================
  // ENHANCED: renderAdvancementsTab with better UX
  // ============================================================================
  const renderAdvancementsTab = () => {
    const canEdit = canEditField('advancements');

    return (
      <>
        <div style={styles.contentSection}>
          <h3 style={styles.sectionTitle}>
            <DollarSign size={26} />
            {t.advancements.title}
            {!canEdit && (
              <span style={{
                fontSize: '12px',
                fontWeight: 'normal',
                marginLeft: '12px',
                padding: '4px 12px',
                borderRadius: '6px',
                background: theme === 'dark' ? 'rgba(251, 191, 36, 0.15)' : 'rgba(251, 191, 36, 0.1)',
                color: theme === 'dark' ? '#fbbf24' : '#d97706',
              }}>
                {language === 'ar' ? '(للقراءة فقط)' : '(Read-only)'}
              </span>
            )}
          </h3>

          {/* Effective Date Field */}
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>{t.advancements.effectiveDate}</label>
              <input
                type="date"
                value={config.advancementEffectiveDate || new Date().toISOString().split('T')[0]}
                onChange={(e) =>
                  canEdit && isCreatingNewConfig && setConfig((prev) => ({ ...prev, advancementEffectiveDate: e.target.value }))
                }
                disabled={!canEdit || !isCreatingNewConfig || isEditingArchive}
                style={{
                  ...getFieldStyle('advancements', styles.input),
                  ...(!isCreatingNewConfig ? { opacity: 0.75, cursor: 'not-allowed' } : {}),
                }}
              />
            </div>
          </div>

          <div style={styles.toggleContainer}>
            <label style={styles.label}>
              {t.advancements.enableAdvancements}
            </label>
            <input
              type="checkbox"
              checked={config.advancementEnabled || false}
              onChange={(e) =>
                canEdit && setConfig({ ...config, advancementEnabled: e.target.checked })
              }
              disabled={!canEdit}
              style={{ ...styles.checkbox, cursor: canEdit ? 'pointer' : 'not-allowed', opacity: canEdit ? 1 : 0.6 }}
            />
          </div>

          {config.advancementEnabled && (
            <>
              {/* Basic Limits */}
              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    {t.advancements.minMonthsBeforeRequest}
                  </label>
                  <input
                    type="text"
                    min="0"
                    value={config.minMonthsOfService}
                    onChange={(e) =>
                      setConfig({
                        ...config,
                        minMonthsOfService: e.target.value
                          ? parseInt(e.target.value)
                          : null,
                      })
                    }
                    style={styles.input}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    {t.advancements.maxAdvancementPercentage}
                  </label>
                  <input
                    type="text"
                    min="0"
                    max="100"

                    value={config.maxAdvancementPercentOfSalary}
                    onChange={(e) =>
                      setConfig({
                        ...config,
                        maxAdvancementPercentOfSalary: e.target.value
                          ? parseFloat(e.target.value)
                          : null,
                      })
                    }
                    style={styles.input}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    {t.advancements.maxAdvancementAmount} (EGP)
                  </label>
                  <input
                    type="text"
                    min="0"
                    //step="0.01"
                    value={config.maxAdvancementAmount}
                    onChange={(e) =>
                      setConfig({
                        ...config,
                        maxAdvancementAmount: e.target.value
                          ? parseFloat(e.target.value)
                          : null,
                      })
                    }
                    style={styles.input}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    {t.advancements.maxActiveAdvancements}
                  </label>
                  <input
                    type="text"
                    min="1"
                    value={config.maxActiveAdvancements}
                    onChange={(e) =>
                      setConfig({
                        ...config,
                        maxActiveAdvancements: e.target.value
                          ? parseInt(e.target.value)
                          : null,
                      })
                    }
                    style={styles.input}
                  />
                </div>
              </div>

              {/* Installment Settings */}
              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Min Installments</label>
                  <input
                    type="text"
                    min="1"
                    max="24"
                    value={config.minInstallments ?? 1}
                    onChange={(e) =>
                      canEdit && setConfig({
                        ...config,
                        minInstallments: e.target.value
                          ? parseInt(e.target.value)
                          : null,
                      })
                    }
                    disabled={!canEdit}
                    style={getFieldStyle('advancements', styles.input)}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Max Installments</label>
                  <input
                    type="text"
                    min="1"
                    max="24"
                    value={config.maxInstallments}
                    onChange={(e) =>
                      canEdit && setConfig({
                        ...config,
                        maxInstallments: e.target.value
                          ? parseInt(e.target.value)
                          : null,
                      })
                    }
                    disabled={!canEdit}
                    style={getFieldStyle('advancements', styles.input)}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Default Installments</label>
                  <input
                    type="text"
                    min="1"
                    max="24"
                    value={config.defaultInstallments}
                    onChange={(e) =>
                      canEdit && setConfig({
                        ...config,
                        defaultInstallments: e.target.value
                          ? parseInt(e.target.value)
                          : null,
                      })
                    }
                    disabled={!canEdit}
                    style={getFieldStyle('advancements', styles.input)}
                  />
                </div>
              </div>

              {/* Advanced Settings */}
              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    Auto-Approve Under Amount (EGP)
                  </label>
                  <input
                    type="text"
                    min="0"
                    step="0.01"
                    value={config.autoApproveUnderAmount ?? 0}
                    onChange={(e) =>
                      canEdit && setConfig({
                        ...config,
                        autoApproveUnderAmount: e.target.value
                          ? parseFloat(e.target.value)
                          : null,
                      })
                    }
                    disabled={!canEdit}
                    style={getFieldStyle('advancements', styles.input)}
                    placeholder="0 = disabled"
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Emergency Max Amount (EGP)</label>
                  <input
                    type="text"
                    min="0"
                    step="0.01"
                    value={config.emergencyMaxAmount}
                    onChange={(e) =>
                      canEdit && setConfig({
                        ...config,
                        emergencyMaxAmount: e.target.value
                          ? parseFloat(e.target.value)
                          : null,
                      })
                    }
                    disabled={!canEdit}
                    style={getFieldStyle('advancements', styles.input)}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Processing Days</label>
                  <input
                    type="text"
                    min="1"
                    value={config.advancementProcessingDays}
                    onChange={(e) =>
                      canEdit && setConfig({
                        ...config,
                        advancementProcessingDays: e.target.value
                          ? parseInt(e.target.value)
                          : null,
                      })
                    }
                    disabled={!canEdit}
                    style={getFieldStyle('advancements', styles.input)}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    Deduction Start Delay (Months)
                  </label>
                  <input
                    type="text"
                    min="0"
                    value={config.deductionStartDelay}
                    onChange={(e) =>
                      canEdit && setConfig({
                        ...config,
                        deductionStartDelay: e.target.value
                          ? parseInt(e.target.value)
                          : null,
                      })
                    }
                    disabled={!canEdit}
                    style={getFieldStyle('advancements', styles.input)}
                  />
                </div>
              </div>

              {/* Approval Settings */}
              <div style={styles.toggleContainer}>
                <label style={styles.label}>Require Manager Approval</label>
                <input
                  type="checkbox"
                  checked={config.requireManagerApproval}
                  onChange={(e) =>
                    canEdit && setConfig({
                      ...config,
                      requireManagerApproval: e.target.checked,
                    })
                  }
                  disabled={!canEdit}
                  style={{ ...styles.checkbox, cursor: canEdit ? 'pointer' : 'not-allowed', opacity: canEdit ? 1 : 0.6 }}
                />
              </div>

              <div style={styles.toggleContainer}>
                <label style={styles.label}>Require HR Approval</label>
                <input
                  type="checkbox"
                  checked={config.requireHRApproval}
                  onChange={(e) =>
                    canEdit && setConfig({
                      ...config,
                      requireHRApproval: e.target.checked,
                    })
                  }
                  disabled={!canEdit}
                  style={{ ...styles.checkbox, cursor: canEdit ? 'pointer' : 'not-allowed', opacity: canEdit ? 1 : 0.6 }}
                />
              </div>

              <div style={styles.toggleContainer}>
                <label style={styles.label}>Allow Multiple Active Requests</label>
                <input
                  type="checkbox"
                  checked={!config.blockAdvancementIfPending}
                  onChange={(e) =>
                    canEdit && setConfig({
                      ...config,
                      blockAdvancementIfPending: !e.target.checked,
                    })
                  }
                  disabled={!canEdit}
                  style={{ ...styles.checkbox, cursor: canEdit ? 'pointer' : 'not-allowed', opacity: canEdit ? 1 : 0.6 }}
                />
              </div>

              <div style={styles.toggleContainer}>
                <label style={styles.label}>Allow Emergency Advancements</label>
                <input
                  type="checkbox"
                  checked={config.allowEmergencyAdvancement}
                  onChange={(e) =>
                    canEdit && setConfig({
                      ...config,
                      allowEmergencyAdvancements: e.target.checked,
                    })
                  }
                  disabled={!canEdit}
                  style={{ ...styles.checkbox, cursor: canEdit ? 'pointer' : 'not-allowed', opacity: canEdit ? 1 : 0.6 }}
                />
              </div>

              <div style={styles.toggleContainer}>
                <label style={styles.label}>Block if Overdue Repayments</label>
                <input
                  type="checkbox"
                  checked={config.blockAdvancementIfOverdue}
                  onChange={(e) =>
                    canEdit && setConfig({
                      ...config,
                      blockAdvancementIfOverdue: e.target.checked,
                    })
                  }
                  disabled={!canEdit}
                  style={{ ...styles.checkbox, cursor: canEdit ? 'pointer' : 'not-allowed', opacity: canEdit ? 1 : 0.6 }}
                />
              </div>

              <div style={styles.toggleContainer}>
                <label style={styles.label}>Advancement Notifications</label>
                <input
                  type="checkbox"
                  checked={config.advancementNotifications}
                  onChange={(e) =>
                    canEdit && setConfig({
                      ...config,
                      advancementNotifications: e.target.checked,
                    })
                  }
                  disabled={!canEdit}
                  style={{ ...styles.checkbox, cursor: canEdit ? 'pointer' : 'not-allowed', opacity: canEdit ? 1 : 0.6 }}
                />
              </div>
            </>
          )}
        </div>
      </>
    );
  };
  const renderCompanyTab = () => {
    const canEdit = canEditField('company');

    return (
      <div style={styles.contentSection}>
        <h3 style={styles.sectionTitle}>
          <Building size={26} />
          {t.company.title}
          {!canEdit && (
            <span style={{
              fontSize: '12px',
              fontWeight: 'normal',
              marginLeft: '12px',
              padding: '4px 12px',
              borderRadius: '6px',
              background: theme === 'dark' ? 'rgba(251, 191, 36, 0.15)' : 'rgba(251, 191, 36, 0.1)',
              color: theme === 'dark' ? '#fbbf24' : '#d97706',
            }}>
              {language === 'ar' ? '(للقراءة فقط)' : '(Read-only)'}
            </span>
          )}
        </h3>

        {/* Form Inputs */}
        <div style={styles.formRow}>
          <div style={styles.formGroup}>
            <label style={styles.label}>{t.company.companyName}</label>
            <input
              type="text"
              value={
                config.companyName || selectedCompanyName || t.company.notSet
              }
              readOnly
              style={{ ...styles.input, opacity: 0.7 }}
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>{t.company.industry}</label>
            <input
              type="text"
              value={config.industry || t.company.notSet}
              onChange={(e) => canEdit && setConfig({ ...config, industry: e.target.value })}
              disabled={!canEdit}
              style={getFieldStyle('company', styles.input)}
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>{t.company.email}</label>
            <input
              type="email"
              value={config.email || ""}
              onChange={(e) => canEdit && setConfig({ ...config, email: e.target.value })}
              disabled={!canEdit}
              style={getFieldStyle('company', styles.input)}
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>{t.company.address}</label>
            <input
              type="text"
              value={config.address || ""}
              onChange={(e) => canEdit && setConfig({ ...config, address: e.target.value })}
              disabled={!canEdit}
              style={getFieldStyle('company', styles.input)}
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>{t.company.website}</label>
            <input
              type="url"
              value={config.website || ""}
              onChange={(e) => canEdit && setConfig({ ...config, website: e.target.value })}
              disabled={!canEdit}
              style={getFieldStyle('company', styles.input)}
            />
          </div>
        </div>

        {/* Stats Grid - خارج formRow */}
        <div style={styles.statsGrid}>
          <div style={styles.statCard}>
            <div
              style={{
                ...styles.statIconWrapper,
                background: "linear-gradient(135deg, #2e7cba, #2e7cba)",
              }}
            >
              <Users size={22} />
            </div>
            <div>
              <div style={styles.statValue}>{detectedCompanies.length || 0}</div>
              <div style={styles.statLabel}>{t.totalCompanies}</div>
            </div>
          </div>

          <div style={styles.statCard}>
            <div
              style={{
                ...styles.statIconWrapper,
                background: "linear-gradient(135deg, #10b981, #059669)",
              }}
            >
              <CheckCircle size={22} />
            </div>
            <div>
              <div style={styles.statValue}>
                {detectedCompanies.filter((c) => c.isConfigured).length || 0}
              </div>
              <div style={styles.statLabel}>{t.configuredCompanies}</div>
            </div>
          </div>

          <div style={styles.statCard}>
            <div
              style={{
                ...styles.statIconWrapper,
                background: "linear-gradient(135deg, #f59e0b, #d97706)",
              }}
            >
              <Building size={22} />
            </div>
            <div>
              <div style={styles.statValue}>{selectedCompanyName ? 1 : 0}</div>
              <div style={styles.statLabel}>{t.selectedCompanyLabel}</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ============================================================================
  // SIMPLE UPDATE - Just replace your renderAttendanceTab function with this
  // No modals, no extra complexity - just an inline department selector
  // ============================================================================

  // ============================================================================
  // COMPLETE REWRITE: renderAttendanceTab with Department-Specific Rules
  // ============================================================================

  const renderAttendanceTab = () => {

    const canEdit = canEditField('attendance');

    // Defensive: always treat departmentRules as an object
    const departmentRules = (config && typeof config.departmentRules === 'object' && config.departmentRules !== null) ? config.departmentRules : {};

    // Helper to get current attendance values (department-specific or company-wide)
    const getAttendanceValue = (field) => {
      if (
        config.department &&
        departmentRules &&
        typeof departmentRules === 'object' &&
        Object.prototype.hasOwnProperty.call(departmentRules, config.department) &&
        departmentRules[config.department]
      ) {
        const deptRules = departmentRules[config.department];
        return deptRules && deptRules[field] !== undefined
          ? deptRules[field]
          : config[field];
      }
      return config[field];
    };

    // Helper to update attendance fields (department-specific or company-wide)
    const handleAttendanceChange = (field, value) => {
      if (config.department) {
        // ARCHIVE MODE: Only update departmentRules[department], not top-level fields
        if (isEditingArchive && selectedArchiveId && archivedConfigs.length > 0) {
          setConfig((prev) => ({
            ...prev,
            departmentRules: {
              ...prev.departmentRules,
              [prev.department]: {
                ...(prev.departmentRules[prev.department] || {}),
                [field]: value,
              },
            },
          }));
        } else {
          // LIVE MODE: update both departmentRules and top-level fields for UI
          setConfig((prev) => ({
            ...prev,
            [field]: value,
            departmentRules: {
              ...prev.departmentRules,
              [prev.department]: {
                ...(prev.departmentRules[prev.department] || {}),
                [field]: value,
              },
            },
          }));
        }
      } else {
        // Update company-wide config
        setConfig((prev) => ({ ...prev, [field]: value }));
      }
    };

    // Handle department selection change
    // ============================================================================
    // FIX: handleDepartmentChange — Archive mode department rules lookup
    //
    // ROOT CAUSE:
    //   Archive records store department-specific rules inside `attendanceRules`
    //   (a JSON column, same as CompanyConfiguration.attendanceRules), but the
    //   archive-mode branch was reading `selectedArchive.departmentRules` which
    //   is NEVER populated from the backend (it's a frontend-only state key).
    //
    // REPLACE the entire handleDepartmentChange function with the version below.
    // ============================================================================

    const handleDepartmentChange = async (selectedDept) => {
      if (!selectedDept) {
        // ── Reset to company-wide settings ──────────────────────────────────────
        if (isEditingArchive && selectedArchiveId && archivedConfigs.length > 0) {
          const selectedArchive = archivedConfigs.find(a => a.id === selectedArchiveId);
          if (selectedArchive) {
            setConfig({
              ...selectedArchive,
              department: "",
              // Rebuild departmentRules from the archive's attendanceRules JSON
              departmentRules: (() => {
                if (!selectedArchive.attendanceRules) return {};
                return typeof selectedArchive.attendanceRules === 'string'
                  ? JSON.parse(selectedArchive.attendanceRules)
                  : selectedArchive.attendanceRules;
              })(),
            });
          }
        } else {
          setConfig((prev) => ({ ...prev, department: "" }));
          if (selectedCompanyName) {
            setLoading(true);
            try {
              await loadConfiguration(selectedCompanyName);
            } catch (e) {
              console.error("Failed to reload company-wide config", e);
            } finally {
              setLoading(false);
            }
          }
        }
        return;
      }

      // ── ARCHIVE MODE ────────────────────────────────────────────────────────────
      if (isEditingArchive && selectedArchiveId && archivedConfigs.length > 0) {
        const selectedArchive = archivedConfigs.find(a => a.id === selectedArchiveId);

        // Parse attendanceRules from the archive row (same JSON column used in live config)
        const archiveAttendanceRules = (() => {
          if (!selectedArchive?.attendanceRules) return {};
          return typeof selectedArchive.attendanceRules === 'string'
            ? JSON.parse(selectedArchive.attendanceRules)
            : selectedArchive.attendanceRules;
        })();

        const archiveDeptRules = archiveAttendanceRules[selectedDept];

        if (archiveDeptRules) {
          // ✅ Department-specific rules found inside the archive's attendanceRules JSON
          console.log(`✅ [Archive mode] Found dept rules for "${selectedDept}" in archive ${selectedArchiveId}`);
          setConfig(prev => ({
            ...prev,
            ...selectedArchive,
            department: selectedDept,
            departmentRules: archiveAttendanceRules,           // full map for UI indicators
            workStartTime: archiveDeptRules.workStartTime || selectedArchive.workStartTime,
            workEndTime: archiveDeptRules.workEndTime || selectedArchive.workEndTime,
            workingHoursPerDay: archiveDeptRules.workingHoursPerDay ?? selectedArchive.workingHoursPerDay,
            workingDaysPerWeek: archiveDeptRules.workingDaysPerWeek ?? selectedArchive.workingDaysPerWeek,
            absenceDeductionPerDay: archiveDeptRules.absenceDeductionPerDay ?? selectedArchive.absenceDeductionPerDay,
            lateDeductionRules: archiveDeptRules.lateDeductionRules ?? selectedArchive.lateDeductionRules,
            overtimeEnabled: archiveDeptRules.overtimeEnabled ?? selectedArchive.overtimeEnabled,
            overtimeRate: archiveDeptRules.overtimeRate ?? selectedArchive.overtimeRate,
            overtimeStartAfter: archiveDeptRules.overtimeStartAfter ?? selectedArchive.overtimeStartAfter,
            maxOvertimeHoursPerDay: archiveDeptRules.maxOvertimeHoursPerDay ?? selectedArchive.maxOvertimeHoursPerDay,
            weekendDays: archiveDeptRules.weekendDays || selectedArchive.weekendDays,
            halfDayRequestThreshold: archiveDeptRules.halfDayRequestThreshold ?? selectedArchive.halfDayRequestThreshold,
          }));
        } else {
          // No department-specific rules in this archive → fall back to archive company-wide values
          console.log(`ℹ️ [Archive mode] No dept rules for "${selectedDept}" in archive ${selectedArchiveId}, using archive company-wide config`);
          setConfig(prev => ({
            ...prev,
            ...selectedArchive,
            department: selectedDept,
            departmentRules: archiveAttendanceRules,
          }));
        }
        return;
      }

      // ── LIVE MODE ────────────────────────────────────────────────────────────────
      const departmentRules = (config && typeof config.departmentRules === 'object' && config.departmentRules !== null)
        ? config.departmentRules
        : {};

      if (departmentRules[selectedDept]) {
        const deptRules = departmentRules[selectedDept];
        setConfig((prev) => ({
          ...prev,
          department: selectedDept,
          workStartTime: deptRules.workStartTime || prev.workStartTime,
          workEndTime: deptRules.workEndTime || prev.workEndTime,
          workingHoursPerDay: deptRules.workingHoursPerDay ?? prev.workingHoursPerDay,
          workingDaysPerWeek: deptRules.workingDaysPerWeek ?? prev.workingDaysPerWeek,
          absenceDeductionPerDay: deptRules.absenceDeductionPerDay ?? prev.absenceDeductionPerDay,
          lateDeductionRules: deptRules.lateDeductionRules ?? prev.lateDeductionRules,
          overtimeEnabled: deptRules.overtimeEnabled ?? prev.overtimeEnabled,
          overtimeRate: deptRules.overtimeRate ?? prev.overtimeRate,
          overtimeStartAfter: deptRules.overtimeStartAfter ?? prev.overtimeStartAfter,
          maxOvertimeHoursPerDay: deptRules.maxOvertimeHoursPerDay ?? prev.maxOvertimeHoursPerDay,
          weekendDays: deptRules.weekendDays || prev.weekendDays,
          halfDayRequestThreshold: deptRules.halfDayRequestThreshold ?? prev.halfDayRequestThreshold,
        }));
      } else {
        // Fetch from backend as fallback
        try {
          const clientName = profile?.clientName || localStorage.getItem("clientName");
          const response = await apiFetch(
            `${BASE_URL}/api/company-config/${clientName}/${encodeURIComponent(selectedCompanyName)}/departments/${encodeURIComponent(selectedDept)}`,
          );
          if (response.ok) {
            const data = await response.json();
            if (data.success && data.data.rules) {
              const deptRules = data.data.rules;
              setConfig((prev) => ({
                ...prev,
                department: selectedDept,
                departmentRules: { ...prev.departmentRules, [selectedDept]: deptRules },
                workStartTime: deptRules.workStartTime || prev.workStartTime,
                workEndTime: deptRules.workEndTime || prev.workEndTime,
                workingHoursPerDay: deptRules.workingHoursPerDay ?? prev.workingHoursPerDay,
                workingDaysPerWeek: deptRules.workingDaysPerWeek ?? prev.workingDaysPerWeek,
                absenceDeductionPerDay: deptRules.absenceDeductionPerDay ?? prev.absenceDeductionPerDay,
                lateDeductionRules: deptRules.lateDeductionRules ?? prev.lateDeductionRules,
                overtimeEnabled: deptRules.overtimeEnabled ?? prev.overtimeEnabled,
                overtimeRate: deptRules.overtimeRate ?? prev.overtimeRate,
                overtimeStartAfter: deptRules.overtimeStartAfter ?? prev.overtimeStartAfter,
                maxOvertimeHoursPerDay: deptRules.maxOvertimeHoursPerDay ?? prev.maxOvertimeHoursPerDay,
                weekendDays: deptRules.weekendDays || prev.weekendDays,
                halfDayRequestThreshold: deptRules.halfDayRequestThreshold ?? prev.halfDayRequestThreshold,
              }));
            }
          }
        } catch (error) {
          console.error("❌ Error loading rules for department:", error);
        }
      }
    };

    const fmtDate = (v) => {
      if (!v) return '—';
      try { return new Date(v).toISOString().split('T')[0]; } catch { return '—'; }
    };
    const selectedArchive = archivedConfigs.find((a) => a.id === selectedArchiveId) || null;

    return (
      <>
        {/* ===== ARCHIVE PICKER BAR ===== */}
        <div style={{
          ...styles.contentSection,
          marginBottom: 16,
          padding: '14px 20px',
          background: isEditingArchive
            ? (theme === 'dark' ? 'rgba(245,158,11,0.09)' : 'rgba(255,251,235,1)')
            : styles.contentSection.background,
          border: isEditingArchive ? '2px solid #f59e0b' : styles.contentSection.border,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <FileText size={17} color="#f59e0b" />
            <span style={{ fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap', color: theme === 'dark' ? '#fbbf24' : '#92400e' }}>
              {isRTL ? 'التاريخ:' : 'History:'}
            </span>
            <div style={{ flex: 1, minWidth: 240 }}>
              <ReusableDropdown
                items={archivedConfigs}
                value={selectedArchiveId ? String(selectedArchiveId) : ''}
                getItemKey={(item) => String(item.id)}
                getItemLabel={(item) => {
                  const d = fmtDate(item.attendanceEffectiveDate || item.effectiveDate);
                  return d + (item.reason ? '  •  ' + item.reason : '') + '  (ID: ' + item.id + ')';
                }}
                renderItem={(item) => {
                  const d = fmtDate(item.attendanceEffectiveDate || item.effectiveDate);
                  return (
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 13 }}>{d}</div>
                      {item.reason && (
                        <div style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }}>
                          {item.reason}
                        </div>
                      )}
                      <div style={{ fontSize: 11, opacity: 0.5, marginTop: 1 }}>ID: {item.id}</div>
                    </div>
                  );
                }}
                onChange={(id) => {
                  if (!id) { setSelectedArchiveId(null); setIsEditingArchive(false); return; }
                  const archive = archivedConfigs.find((a) => String(a.id) === String(id));
                  if (archive) selectArchive(archive);
                }}
                onClear={() => { setSelectedArchiveId(null); setIsEditingArchive(false); }}
                placeholder={
                  archivesLoading
                    ? (isRTL ? 'جار التحميل…' : 'Loading archives…')
                    : archivedConfigs.length === 0
                      ? (isRTL ? 'لا يوجد أرشيف' : 'No archives found')
                      : (isRTL ? '— التكوين الحالي (بدون أرشيف) —' : '— Live configuration (current) —')
                }
                disabled={archivesLoading || archivedConfigs.length === 0}
                theme={theme}
                language={language}
                showAllOnArrowClick={true}
                showClear={!!selectedArchiveId}
                inputStyle={{
                  borderRadius: 8,
                  border: isEditingArchive ? '1.5px solid #f59e0b' : '1px solid rgba(148,163,184,0.3)',
                  fontSize: 13,
                  padding: '7px 40px 7px 12px',
                  background: isEditingArchive
                    ? (theme === 'dark' ? 'rgba(245,158,11,0.08)' : 'rgba(255,251,235,0.8)')
                    : (theme === 'dark' ? 'rgba(71, 85, 105, 0.4)' : 'rgba(226, 232, 240, 0.6)'),
                  color: theme === 'dark' ? '#f1f5f9' : '#334155',
                }}
              />
            </div>
            <button
              onClick={() => loadArchives(selectedCompanyName)}
              title={isRTL ? 'تحديث' : 'Refresh'}
              style={{ padding: '7px 13px', borderRadius: 8, border: '1px solid #ccc', background: 'transparent', cursor: 'pointer', fontSize: 14, color: theme === 'dark' ? '#f0f0f0' : '#555' }}
              disabled={archivesLoading}
            >
              {archivesLoading ? (isRTL ? 'جار التحميل...' : 'Loading...') : (isRTL ? 'تحديث' : 'Refresh')}
            </button>
          </div>

          {/* Archive mode active banner */}
          {isEditingArchive && selectedArchive && (
            <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13, color: '#d97706', fontWeight: 600 }}>
                {isRTL
                  ? 'وضع تعديل الأرشيف — التغييرات تُحفظ في الأرشيف فقط وليس في التكوين الحالي'
                  : 'Archive edit mode — changes save to archive only, not to live config'}
              </span>
              <span style={{ fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 6, background: 'rgba(245,158,11,0.18)', color: '#d97706' }}>
                {isRTL ? 'تاريخ السريان:' : 'Effective:'} {fmtDate(selectedArchive.attendanceEffectiveDate || selectedArchive.effectiveDate)}
              </span>
              <button
                onClick={() => updateArchive(selectedArchiveId)}
                disabled={loading}
                style={{ padding: '6px 16px', borderRadius: 8, border: 'none', background: '#f59e0b', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
              >
                {loading ? (isRTL ? 'جار الحفظ…' : 'Saving…') : (isRTL ? 'حفظ في الأرشيف' : 'Save to Archive')}
              </button>
              <button
                onClick={() => { setIsEditingArchive(false); setSelectedArchiveId(null); }}
                style={{ padding: '6px 16px', borderRadius: 8, border: '1.5px solid #ccc', background: 'transparent', fontWeight: 700, fontSize: 13, cursor: 'pointer', color: theme === 'dark' ? '#f0f0f0' : '#555' }}
              >
                {isRTL ? 'خروج من الأرشيف' : 'Exit Archive'}
              </button>
            </div>
          )}
        </div>

        {/* ===== ATTENDANCE RULES ===== */}
        <div style={{
          ...styles.contentSection,
          border: styles.contentSection.border,
          boxShadow: styles.contentSection.boxShadow,
        }}>
          <h3 style={styles.sectionTitle}>
            <Clock size={26} />
            {t.attendance.title}
            {isEditingArchive && (
              <span style={{ fontSize: '12px', fontWeight: 800, marginLeft: '12px', padding: '4px 12px', borderRadius: '6px', background: 'rgba(245,158,11,0.18)', color: '#d97706' }}>
                {isRTL ? 'التاريخ' : 'HISTORY'}
              </span>
            )}
            {!canEdit && (
              <span style={{
                fontSize: '12px',
                fontWeight: 'normal',
                marginLeft: '12px',
                padding: '4px 12px',
                borderRadius: '6px',
                background: theme === 'dark' ? 'rgba(251, 191, 36, 0.15)' : 'rgba(251, 191, 36, 0.1)',
                color: theme === 'dark' ? '#fbbf24' : '#d97706',
              }}>
                {language === 'ar' ? '(للقراءة فقط)' : '(Read-only)'}
              </span>
            )}
          </h3>
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>
                {isRTL ? "القسم" : "Department"}
                {config.department && (
                  <span
                    style={{
                      marginLeft: isRTL ? "0" : "10px",
                      marginRight: isRTL ? "10px" : "0",
                      padding: "4px 10px",
                      borderRadius: "6px",
                      fontSize: "11px",
                      fontWeight: "800",
                      background: "rgba(46, 124, 186, 0.15)",
                      color: "#2e7cba",
                      textTransform: "uppercase",
                    }}
                  >
                    {isRTL ? "قواعد خاصة" : "Dept Rules"}
                  </span>
                )}
              </label>
              <ReusableDropdown
                items={[
                  { value: "", label: loadingDepartments ? (isRTL ? "جاري التحميل..." : "Loading departments...") : departments.length === 0 ? (isRTL ? "لا توجد أقسام" : "No departments found") : (isRTL ? "اختر قسم (اختياري - لقواعد خاصة)" : "Select Department (Optional - For Specific Rules)") },
                  { value: "", label: isRTL ? "--- إعدادات الشركة العامة ---" : "--- Company-Wide Settings ---" },
                  ...departments.map((dept) => ({ value: dept, label: dept + (config.departmentRules[dept] ? " ✓" : "") }))
                ]}
                value={config.department || ""}
                onChange={(value) => handleDepartmentChange(value)}
                onSelect={(item) => handleDepartmentChange(item.value)}
                placeholder={isRTL ? "اختر قسم" : "Select Department"}
                theme={theme}
                language={language}
                disabled={loadingDepartments || !selectedCompanyName}
                getItemKey={(item) => item.value}
                getItemLabel={(item) => item.label}
                maxItems={20}
              />

              {/* Info Text */}
              <p
                style={{
                  fontSize: "12px",
                  color:
                    theme === "dark"
                      ? "rgba(255,255,255,0.6)"
                      : "rgba(15,23,42,0.6)",
                  marginTop: "6px",
                  fontStyle: "italic",
                }}
              >
                {config.department
                  ? isRTL
                    ? `تعديل القواعد الخاصة بقسم: ${config.department}`
                    : `Editing rules specific to: ${config.department}`
                  : isRTL
                    ? "تعديل القواعد العامة لجميع الأقسام"
                    : "Editing company-wide rules for all departments"}
              </p>
            </div>
          </div>

          {/* Effective Date Field */}
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>
                {t.attendance.effectiveDate}
                {isCreatingNewConfig ? (
                  <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 5, background: 'rgba(245,158,11,0.18)', color: '#d97706' }}>
                    {isRTL ? 'قابل للتعديل' : 'EDITABLE'}
                  </span>
                ) : (
                  <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 5, background: 'rgba(148,163,184,0.18)', color: '#64748b' }}>
                    {isRTL ? 'مقفل' : 'LOCKED'}
                  </span>
                )}
              </label>
              <input
                type="date"
                value={config.attendanceEffectiveDate || new Date().toISOString().split('T')[0]}
                onChange={(e) =>
                  canEdit && isCreatingNewConfig && setConfig((prev) => ({ ...prev, attendanceEffectiveDate: e.target.value }))
                }
                disabled={!canEdit || !isCreatingNewConfig || isEditingArchive}
                style={{
                  ...getFieldStyle('attendance', styles.input),
                  ...(!isCreatingNewConfig ? { opacity: 0.75, cursor: 'not-allowed' } : {}),
                  ...(isEditingArchive ? { borderColor: '#f59e0b' } : {}),
                }}
              />
            </div>
          </div>

          {/* Working Hours Fields */}
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>{t.attendance.workStartTime}</label>
              <input
                type="time"
                value={getAttendanceValue("workStartTime")}
                onChange={(e) =>
                  canEdit && handleAttendanceChange("workStartTime", e.target.value)
                }
                disabled={!canEdit}
                style={getFieldStyle('attendance', styles.input)}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>{t.attendance.workEndTime}</label>
              <input
                type="time"
                value={getAttendanceValue("workEndTime")}
                onChange={(e) =>
                  canEdit && handleAttendanceChange("workEndTime", e.target.value)
                }
                disabled={!canEdit}
                style={getFieldStyle('attendance', styles.input)}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>
                {t.attendance.workingHoursPerDay}
              </label>
              <input
                type="text"
                min="1"
                max="24"
                value={getAttendanceValue("workingHoursPerDay")}
                onChange={(e) =>
                  handleAttendanceChange(
                    "workingHoursPerDay",
                    parseInt(e.target.value)
                  )
                }
                style={styles.input}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>
                {t.attendance.workingDaysPerWeek}
              </label>
              <input
                type="text"
                min="1"
                max="7"
                value={getAttendanceValue("workingDaysPerWeek")}
                onChange={(e) =>
                  handleAttendanceChange(
                    "workingDaysPerWeek",
                    parseInt(e.target.value)
                  )
                }
                style={styles.input}
              />
            </div>
          </div>

          {/* Weekend Days Selector */}
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>
                {isRTL ? "أيام العطلة الأسبوعية" : "Weekend Days"}
              </label>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "10px",
                  marginTop: "8px",
                }}
              >
                {[
                  "Sunday",
                  "Monday",
                  "Tuesday",
                  "Wednesday",
                  "Thursday",
                  "Friday",
                  "Saturday",
                ].map((day) => {
                  const isSelected =
                    getAttendanceValue("weekendDays").includes(day);
                  return (
                    <button
                      key={day}
                      type="button"
                      onClick={() => {
                        const currentWeekends =
                          getAttendanceValue("weekendDays");
                        const newWeekends = isSelected
                          ? currentWeekends.filter((d) => d !== day)
                          : [...currentWeekends, day];
                        handleAttendanceChange("weekendDays", newWeekends);
                      }}
                      style={{
                        padding: "8px 16px",
                        borderRadius: "8px",
                        border: isSelected
                          ? "2px solid #2e7cba"
                          : theme === "dark"
                            ? "2px solid rgba(255,255,255,0.2)"
                            : "2px solid rgba(0,0,0,0.15)",
                        background: isSelected
                          ? "rgba(46, 124, 186, 0.2)"
                          : "transparent",
                        color: isSelected
                          ? "#2e7cba"
                          : theme === "dark"
                            ? "#fff"
                            : "#0f172a",
                        fontSize: "13px",
                        fontWeight: isSelected ? "800" : "600",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                      }}
                    >
                      {isRTL
                        ? {
                          Sunday: "الأحد",
                          Monday: "الإثنين",
                          Tuesday: "الثلاثاء",
                          Wednesday: "الأربعاء",
                          Thursday: "الخميس",
                          Friday: "الجمعة",
                          Saturday: "السبت",
                        }[day]
                        : day}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Delete Department Rules Button */}
        {config.department && config.departmentRules[config.department] && (
          <div style={styles.contentSection}>
            <button
              onClick={async () => {
                if (
                  window.confirm(
                    isRTL
                      ? `هل تريد حذف القواعد الخاصة بقسم ${config.department}؟ سيتم استخدام القواعد العامة بدلاً منها.`
                      : `Delete specific rules for ${config.department}? Company-wide rules will be used instead.`
                  )
                ) {
                  try {
                    const clientName = profile?.clientName || localStorage.getItem("clientName");

                    const response = await apiFetch(
                      `${BASE_URL}/api/company-config/${clientName}/${encodeURIComponent(
                        selectedCompanyName
                      )}/departments/${encodeURIComponent(config.department)}`,
                      {
                        method: "DELETE",
                      }
                    );

                    if (response.ok) {
                      // Remove from local state
                      setConfig((prev) => {
                        const newRules = { ...prev.departmentRules };
                        delete newRules[prev.department];
                        return {
                          ...prev,
                          departmentRules: newRules,
                          department: "", // Reset to company-wide
                        };
                      });

                      showAlert(
                        isRTL ? "نجاح" : "Success",
                        isRTL
                          ? "تم حذف القواعد الخاصة بالقسم بنجاح"
                          : "Department rules deleted successfully",
                        'success'
                      );
                    } else {
                      throw new Error("Failed to delete");
                    }
                  } catch (error) {
                    console.error("Error deleting department rules:", error);
                    showAlert(
                      isRTL ? "خطأ" : "Error",
                      isRTL
                        ? "فشل حذف قواعد القسم"
                        : "Failed to delete department rules",
                      'error'
                    );
                  }
                }
              }}
              style={{
                ...styles.removeButton,
                marginTop: "0px",
                width: "100%",
              }}
            >
              <X size={16} />
              {isRTL
                ? `حذف القواعد الخاصة بقسم ${config.department}`
                : `Delete ${config.department} Specific Rules`}
            </button>
          </div>
        )}

        {/* Absence Deduction Section */}
        <div style={styles.contentSection}>
          <h3 style={styles.sectionTitle}>
            <AlertCircle size={26} />
            {t.attendance.absenceDeduction}
          </h3>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>
                {t.attendance.absenceDeduction}
              </label>
              <ReusableDropdown
                items={[
                  { value: 0, label: t.attendance.noDeduction },
                  { value: 0.25, label: t.attendance.quarterDay },
                  { value: 0.5, label: t.attendance.halfDay },
                  { value: 1, label: t.attendance.fullDay },
                  { value: 1.25, label: t.attendance.oneAndQuarterDay },
                  { value: 1.5, label: t.attendance.oneAndHalfDay },
                  { value: 2, label: t.attendance.twoDays },
                ]}
                value={getAttendanceValue("absenceDeductionPerDay")}
                onChange={(value) => handleAttendanceChange("absenceDeductionPerDay", parseFloat(value))}
                onSelect={(item) => handleAttendanceChange("absenceDeductionPerDay", parseFloat(item.value))}
                placeholder={isRTL ? "اختر" : "Select"}
                theme={theme}
                language={language}
                getItemKey={(item) => item.value}
                getItemLabel={(item) => item.label}
                maxItems={10}
              />
            </div>
          </div>

          {/* Default Daily Rate Percentage */}
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>
                {t.attendance.defaultDailyRatePercentage}
              </label>
              <p style={{
                color: theme === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(15,23,42,0.6)',
                fontSize: '12px',
                marginBottom: '8px',
                lineHeight: '1.5',
              }}>
                {t.attendance.defaultDailyRatePercentageDesc}
              </p>
              <PermissionInput
                section="attendance"
                type="number"
                min="0"
                max="1"
                step="0.0001"
                value={getAttendanceValue("defaultDailyRatePercentage") ?? 0}
                onChange={(e) => {
                  const val = parseFloat(e.target.value);
                  if (!isNaN(val) && val >= 0 && val <= 1) {
                    handleAttendanceChange("defaultDailyRatePercentage", val);
                  }
                }}
                style={getFieldStyle('attendance', styles.input)}
              />
            </div>
          </div>
        </div>

        {/* Late Deduction Rules Section - NEW */}
        <div style={styles.contentSection}>
          <h3 style={styles.sectionTitle}>
            <Clock size={26} />
            {t.attendance.lateDeductionRules}
          </h3>

          <p style={{
            color: theme === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(15,23,42,0.7)',
            fontSize: '14px',
            marginBottom: '24px',
            lineHeight: '1.6',
          }}>
            {isRTL
              ? 'حدد نسب الخصم لكل نطاق من دقائق التأخير. على سبيل المثال: من 0 إلى 15 دقيقة = 0% خصم، من 15 إلى 30 دقيقة = 25% من الراتب اليومي'
              : 'Define deduction percentages for different late minute ranges. For example: 0-15 min = 0%, 15-30 min = 25% of daily rate, 30+ min = 100%'}
          </p>

          {getAttendanceValue("lateDeductionRules").map((rule, idx) => {
            const allRules = getAttendanceValue("lateDeductionRules");
            const lateThreshold = allRules[0]?.minutesTo ?? 15; // First rule's minutesTo is the threshold
            const isFirstRule = idx === 0;

            // ✅ Helper: after every change, re-sync all minutesFrom so each rule
            //    starts exactly where the previous one ends.
            const applyAndNormalize = (newRules) => {
              newRules[0].minutesFrom = 0;
              for (let i = 1; i < newRules.length; i++) {
                newRules[i].minutesFrom = newRules[i - 1].minutesTo ?? 0;
              }
              handleAttendanceChange("lateDeductionRules", newRules);
            };

            return (
              <div key={idx} style={styles.leaveRuleContainer}>
                {isFirstRule ? (
                  // ✅ FIRST RULE: Show "Late Threshold Minutes" in a highlighted box
                  <div style={styles.formGroup}>
                    <label style={{
                      ...styles.label,
                      fontWeight: 'bold',
                      color: theme === 'dark' ? '#FFB6C1' : '#C84B6E',
                    }}>
                      {t.attendance.lateThresholdMinutes}
                    </label>
                    <div style={{
                      background: theme === 'dark' ? 'rgba(255,182,193,0.15)' : 'rgba(200,75,110,0.1)',
                      border: `2px solid ${theme === 'dark' ? 'rgba(255,182,193,0.4)' : 'rgba(200,75,110,0.3)'}`,
                      padding: '12px',
                      borderRadius: '8px',
                      textAlign: 'center',
                    }}>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={rule.minutesTo === null ? "" : rule.minutesTo}
                        onChange={(e) => {
                          const newRules = [...getAttendanceValue("lateDeductionRules")];
                          const oldThreshold = newRules[0]?.minutesTo ?? 15;
                          const newThreshold = e.target.value === "" ? null : parseInt(e.target.value);

                          // Update first rule's ceiling
                          newRules[0].minutesTo = newThreshold;

                          // Adjust subsequent rules to maintain the delta, then normalize minutesFrom
                          for (let i = 1; i < newRules.length; i++) {
                            const currentDelta = (newRules[i].minutesTo ?? 0) - (oldThreshold ?? 0);
                            newRules[i].minutesTo = newThreshold === null ? null : newThreshold + currentDelta;
                          }

                          applyAndNormalize(newRules);
                        }}
                        placeholder={isRTL ? "غير محدود" : "Unlimited"}
                        style={{
                          ...styles.input,
                          background: theme === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.5)',
                          fontSize: '16px',
                          fontWeight: 'bold',
                          textAlign: 'center',
                        }}
                        disabled={!canEdit}
                      />
                    </div>
                  </div>
                ) : (
                  // ✅ OTHER RULES: Show "Additional Minutes to Add" (delta from threshold)
                  <div style={styles.formGroup}>
                    <label style={styles.label}>
                      {t.attendance.additionalMinutes}
                    </label>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}>
                      <div style={{ flex: 1 }}>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={
                            rule.minutesTo === null
                              ? ""
                              : (rule.minutesTo - lateThreshold)
                          }
                          onChange={(e) => {
                            const newRules = [...getAttendanceValue("lateDeductionRules")];
                            const deltaMinutes = e.target.value === "" ? null : parseInt(e.target.value);
                            newRules[idx].minutesTo = deltaMinutes === null ? null : lateThreshold + deltaMinutes;
                            applyAndNormalize(newRules);
                          }}
                          placeholder={isRTL ? "غير محدود" : "Unlimited"}
                          style={styles.input}
                          disabled={!canEdit}
                        />
                      </div>
                      <div style={{
                        background: theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: '500',
                        color: theme === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)',
                        minWidth: '100px',
                        textAlign: 'center',
                      }}>
                        {t.attendance.fromMinute}: {lateThreshold}
                      </div>
                      <div style={{
                        fontWeight: 'bold',
                        fontSize: '16px',
                        color: theme === 'dark' ? '#FFB6C1' : '#C84B6E',
                        minWidth: '30px',
                        textAlign: 'center',
                      }}>
                        =
                      </div>
                      <div style={{
                        background: theme === 'dark' ? 'rgba(255,182,193,0.2)' : 'rgba(200,75,110,0.15)',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        color: theme === 'dark' ? '#FFB6C1' : '#C84B6E',
                        minWidth: '100px',
                        textAlign: 'center',
                      }}>
                        {rule.minutesTo === null ? (isRTL ? "غير محدود" : "Unlimited") : rule.minutesTo}
                      </div>
                    </div>
                  </div>
                )}

                <div style={styles.formGroup}>
                  <label style={styles.label}>{t.attendance.deductionPercentage}</label>
                  <input
                    type="text"
                    value={rule.deductionPercentage ?? 0}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '' || /^\d*\.?\d*$/.test(val)) {
                        const newRules = [...getAttendanceValue("lateDeductionRules")];
                        newRules[idx].deductionPercentage = val === '' ? '' : parseFloat(val);
                        handleAttendanceChange("lateDeductionRules", newRules);
                      }
                    }}
                    style={styles.input}
                    disabled={!canEdit}
                  />
                </div>
              </div>
            );
          })}

          <button
            onClick={() => {
              const newRules = [...getAttendanceValue("lateDeductionRules")];
              const lastRule = newRules[newRules.length - 1];
              newRules.push({
                minutesFrom: lastRule.minutesTo ?? 0,
                minutesTo: null,
                deductionPercentage: 0,
              });
              // Normalize all minutesFrom so each rule starts where the previous ends
              newRules[0].minutesFrom = 0;
              for (let i = 1; i < newRules.length; i++) {
                newRules[i].minutesFrom = newRules[i - 1].minutesTo ?? 0;
              }
              handleAttendanceChange("lateDeductionRules", newRules);
            }}
            style={styles.addButton}
            disabled={!canEdit}
          >
            <Plus size={18} />
            {t.attendance.addRule}
          </button>

          {getAttendanceValue("lateDeductionRules").length > 1 && (
            <button
              onClick={() => {
                const newRules = getAttendanceValue("lateDeductionRules").slice(0, -1);
                // Last rule must always have minutesTo = null (unlimited)
                newRules[newRules.length - 1].minutesTo = null;
                handleAttendanceChange("lateDeductionRules", newRules);
              }}
              style={{ ...styles.removeButton, marginTop: "12px", width: "100%" }}
              disabled={!canEdit}
            >
              <X size={16} />
              {t.attendance.removeRule}
            </button>
          )}
        </div>

        
      </>
    );
  };
  const renderOvertimeTab = () => {
    const canEdit = canEditField('overtime');

    return (
      <>
        <div style={styles.contentSection}>
          <h3 style={styles.sectionTitle}>
            <TrendingUp size={26} />
            {t.overtime.title}
            {!canEdit && (
              <span style={{
                fontSize: '12px',
                fontWeight: 'normal',
                marginLeft: '12px',
                padding: '4px 12px',
                borderRadius: '6px',
                background: theme === 'dark' ? 'rgba(251, 191, 36, 0.15)' : 'rgba(251, 191, 36, 0.1)',
                color: theme === 'dark' ? '#fbbf24' : '#d97706',
              }}>
                {language === 'ar' ? '(للقراءة فقط)' : '(Read-only)'}
              </span>
            )}
          </h3>

          {/* Effective Date Field */}
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>{t.overtime.effectiveDate}</label>
              <input
                type="date"
                value={config.overtimeEffectiveDate || new Date().toISOString().split('T')[0]}
                onChange={(e) =>
                  canEdit && isCreatingNewConfig && setConfig((prev) => ({ ...prev, overtimeEffectiveDate: e.target.value }))
                }
                disabled={!canEdit || !isCreatingNewConfig || isEditingArchive}
                style={{
                  ...getFieldStyle('overtime', styles.input),
                  ...(!isCreatingNewConfig ? { opacity: 0.75, cursor: 'not-allowed' } : {}),
                }}
              />
            </div>
          </div>

          <div style={styles.toggleContainer}>
            <label style={styles.label}>{t.overtime.enableOvertime}</label>
            <input
              type="checkbox"
              checked={config.overtimeEnabled}
              onChange={(e) =>
                canEdit && setConfig({ ...config, overtimeEnabled: e.target.checked })
              }
              disabled={!canEdit}
              style={{ ...styles.checkbox, cursor: canEdit ? 'pointer' : 'not-allowed', opacity: canEdit ? 1 : 0.6 }}
            />
          </div>

          {config.overtimeEnabled && (
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>{t.overtime.overtimeRate}</label>
                <input
                  type="text"
                  min="0"
                  step="0.1"
                  value={config.overtimeRate}
                  onChange={(e) =>
                    canEdit && setConfig({
                      ...config,
                      overtimeRate: parseFloat(e.target.value),
                    })
                  }
                  disabled={!canEdit}
                  style={getFieldStyle('overtime', styles.input)}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  {t.overtime.overtimeStartsAfter}
                </label>
                <input
                  type="text"
                  min="0"
                  max="24"
                  value={config.overtimeStartAfter}
                  onChange={(e) =>
                    canEdit && setConfig({
                      ...config,
                      overtimeStartAfter: parseInt(e.target.value),
                    })
                  }
                  disabled={!canEdit}
                  style={getFieldStyle('overtime', styles.input)}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>{t.overtime.maxOvertimePerDay}</label>
                <input
                  type="text"
                  min="0"
                  max="24"
                  value={config.maxOvertimeHoursPerDay}
                  onChange={(e) =>
                    canEdit && setConfig({
                      ...config,
                      maxOvertimeHoursPerDay: parseInt(e.target.value),
                    })
                  }
                  disabled={!canEdit}
                  style={getFieldStyle('overtime', styles.input)}
                />
              </div>
            </div>
          )}

          <div style={styles.toggleContainer}>
            <label style={styles.label}>{t.overtime.approvalRequired}</label>
            <input
              type="checkbox"
              checked={config.overtimeApprovalRequired}
              onChange={(e) =>
                canEdit && setConfig({
                  ...config,
                  overtimeApprovalRequired: e.target.checked,
                })
              }
              disabled={!canEdit}
              style={{ ...styles.checkbox, cursor: canEdit ? 'pointer' : 'not-allowed', opacity: canEdit ? 1 : 0.6 }}
            />
          </div>
        </div>

        <div style={styles.contentSection}>
          <h3 style={styles.sectionTitle}>
            <Bell size={26} />
            {t.overtime.notificationSettings}
          </h3>
          <div style={styles.toggleContainer}>
            <label style={styles.label}>{t.overtime.lateNotifications}</label>
            <input
              type="checkbox"
              checked={config.lateNotification}
              onChange={(e) =>
                canEdit && setConfig({ ...config, lateNotification: e.target.checked })
              }
              disabled={!canEdit}
              style={{ ...styles.checkbox, cursor: canEdit ? 'pointer' : 'not-allowed', opacity: canEdit ? 1 : 0.6 }}
            />
          </div>
          <div style={styles.toggleContainer}>
            <label style={styles.label}>{t.overtime.absenceNotifications}</label>
            <input
              type="checkbox"
              checked={config.absenceNotification}
              onChange={(e) =>
                canEdit && setConfig({ ...config, absenceNotification: e.target.checked })
              }
              disabled={!canEdit}
              style={{ ...styles.checkbox, cursor: canEdit ? 'pointer' : 'not-allowed', opacity: canEdit ? 1 : 0.6 }}
            />
          </div>
          <div style={styles.toggleContainer}>
            <label style={styles.label}>{t.overtime.overtimeNotifications}</label>
            <input
              type="checkbox"
              checked={config.overtimeNotification}
              onChange={(e) =>
                canEdit && setConfig({ ...config, overtimeNotification: e.target.checked })
              }
              disabled={!canEdit}
              style={{ ...styles.checkbox, cursor: canEdit ? 'pointer' : 'not-allowed', opacity: canEdit ? 1 : 0.6 }}
            />
          </div>
          <div style={styles.toggleContainer}>
            <label style={styles.label}>{t.overtime.leaveNotifications}</label>
            <input
              type="checkbox"
              checked={config.leaveNotification}
              onChange={(e) =>
                canEdit && setConfig({ ...config, leaveNotification: e.target.checked })
              }
              disabled={!canEdit}
              style={{ ...styles.checkbox, cursor: canEdit ? 'pointer' : 'not-allowed', opacity: canEdit ? 1 : 0.6 }}
            />
          </div>
        </div>
      </>
    );
  };

  const renderArchiveTab = () => {
    const fmtDate = (v) => {
      if (!v) return '—';
      try { return new Date(v).toISOString().split('T')[0]; } catch { return '—'; }
    };

    const selectedArchive = archivedConfigs.find((a) => a.id === selectedArchiveId) || null;

    return (
      <div style={styles.contentSection}>
        <h3 style={styles.sectionTitle}>
          <FileText size={26} /> Archived Configurations
        </h3>

        {/* Picker Row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          <label style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Select Archive:</label>
          <select
            value={selectedArchiveId || ''}
            onChange={(e) => {
              const id = Number(e.target.value);
              const archive = archivedConfigs.find((a) => a.id === id);
              if (archive) selectArchive(archive);
              else {
                setSelectedArchiveId(null);
                setIsEditingArchive(false);
              }
            }}
            style={{
              flex: 1,
              minWidth: 280,
              padding: '8px 12px',
              borderRadius: 8,
              border: '1px solid #ccc',
              fontSize: 14,
              background: theme === 'dark' ? '#2d2d2d' : '#fff',
              color: theme === 'dark' ? '#f0f0f0' : '#333',
              cursor: 'pointer',
            }}
            disabled={archivesLoading || archivedConfigs.length === 0}
          >
            <option value="">
              {archivesLoading
                ? 'Loading archives…'
                : archivedConfigs.length === 0
                  ? 'No archived configurations found'
                  : '— Choose an archived configuration —'}
            </option>
            {archivedConfigs.map((a) => {
              const attDate = fmtDate(a.attendanceEffectiveDate || a.effectiveDate);
              const label = `${attDate}${a.reason ? '  •  ' + a.reason : ''}  (ID: ${a.id})`;
              return (
                <option key={a.id} value={a.id}>
                  {label}
                </option>
              );
            })}
          </select>

          <button
            onClick={() => loadArchives(selectedCompanyName)}
            style={{ ...styles.resetButton, whiteSpace: 'nowrap' }}
            disabled={archivesLoading}
          >
            {archivesLoading ? 'Loading…' : 'Refresh'}
          </button>
        </div>

        {/* Selected archive summary card */}
        {selectedArchive && (
          <div
            style={{
              padding: '14px 18px',
              borderRadius: 10,
              marginBottom: 20,
              border: isEditingArchive ? '2px solid #f59e0b' : '1px solid #ccc',
              background: isEditingArchive
                ? (theme === 'dark' ? 'rgba(245,158,11,0.1)' : 'rgba(255,251,235,1)')
                : (theme === 'dark' ? '#2d2d2d' : '#f9fafb'),
            }}
          >
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
              <div><span style={{ fontWeight: 600 }}>Archive ID: </span>{selectedArchive.id}</div>
              <div><span style={{ fontWeight: 600 }}>Attendance Effective: </span>{fmtDate(selectedArchive.attendanceEffectiveDate)}</div>
              <div><span style={{ fontWeight: 600 }}>Overtime Effective: </span>{fmtDate(selectedArchive.overtimeEffectiveDate)}</div>
              <div><span style={{ fontWeight: 600 }}>Reason: </span>{selectedArchive.reason || '—'}</div>
              <div><span style={{ fontWeight: 600 }}>Archived By: </span>{selectedArchive.archivedBy || '—'}</div>
              <div><span style={{ fontWeight: 600 }}>Archived On: </span>{fmtDate(selectedArchive.effectiveDate)}</div>
            </div>
            {isEditingArchive && (
              <div style={{ marginTop: 10, fontWeight: 600, color: '#d97706' }}>
                You are editing this archive. Changes will only be saved to the archive — not to the live configuration.
              </div>
            )}
          </div>
        )}

        {/* Action buttons when an archive is loaded for editing */}
        {isEditingArchive && selectedArchiveId && (
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
            <button
              onClick={() => setActiveTab('attendance')}
              style={{ ...styles.saveButton }}
            >
              Edit Attendance Rules
            </button>
            <button
              onClick={() => updateArchive(selectedArchiveId)}
              style={{ ...styles.saveButton, background: '#16a34a' }}
              disabled={loading}
            >
              {loading ? 'Saving…' : '💾 Save Changes to Archive'}
            </button>
            <button
              onClick={() => {
                setIsEditingArchive(false);
                setSelectedArchiveId(null);
                loadConfiguration(selectedCompanyName);
              }}
              style={styles.resetButton}
            >
              ✕ Cancel &amp; Restore Live Config
            </button>
          </div>
        )}

        {/* Hint when nothing is selected */}
        {!selectedArchiveId && !archivesLoading && archivedConfigs.length > 0 && (
          <div style={{ color: '#6b7280', fontStyle: 'italic' }}>
            Select an archive from the dropdown above to view its details and edit it.
          </div>
        )}
      </div>
    );
  };

  const renderLeavesTab = () => {
    const canEdit = canEditField('leaves');

    // Local helpers to mirror attendance behaviour so Leaves tab can reuse
    // the same department-aware getters/setters for halfDayRequestThreshold
    const departmentRules = (config && typeof config.departmentRules === 'object' && config.departmentRules !== null) ? config.departmentRules : {};

    const getAttendanceValue = (field) => {
      if (
        config.department &&
        departmentRules &&
        typeof departmentRules === 'object' &&
        Object.prototype.hasOwnProperty.call(departmentRules, config.department) &&
        departmentRules[config.department]
      ) {
        const deptRules = departmentRules[config.department];
        return deptRules && deptRules[field] !== undefined
          ? deptRules[field]
          : config[field];
      }
      return config[field];
    };

    const handleAttendanceChange = (field, value) => {
      if (config.department) {
        if (isEditingArchive && selectedArchiveId && archivedConfigs.length > 0) {
          setConfig((prev) => ({
            ...prev,
            departmentRules: {
              ...prev.departmentRules,
              [prev.department]: {
                ...(prev.departmentRules[prev.department] || {}),
                [field]: value,
              },
            },
          }));
        } else {
          setConfig((prev) => ({
            ...prev,
            [field]: value,
            departmentRules: {
              ...prev.departmentRules,
              [prev.department]: {
                ...(prev.departmentRules[prev.department] || {}),
                [field]: value,
              },
            },
          }));
        }
      } else {
        setConfig((prev) => ({ ...prev, [field]: value }));
      }
    };

    return (
      <>
        <div style={styles.contentSection}>
          <h3 style={styles.sectionTitle}>
            <Calendar size={26} />
            {t.leaves.title}
            {!canEdit && (
              <span style={{
                fontSize: '12px',
                fontWeight: 'normal',
                marginLeft: '12px',
                padding: '4px 12px',
                borderRadius: '6px',
                background: theme === 'dark' ? 'rgba(251, 191, 36, 0.15)' : 'rgba(251, 191, 36, 0.1)',
                color: theme === 'dark' ? '#fbbf24' : '#d97706',
              }}>
                {language === 'ar' ? '(للقراءة فقط)' : '(Read-only)'}
              </span>
            )}
          </h3>

          {/* Effective Date Field */}
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>{t.leaves.effectiveDate}</label>
              <input
                type="date"
                value={config.leaveEffectiveDate || new Date().toISOString().split('T')[0]}
                onChange={(e) =>
                  canEdit && isCreatingNewConfig && setConfig((prev) => ({ ...prev, leaveEffectiveDate: e.target.value }))
                }
                disabled={!canEdit || !isCreatingNewConfig || isEditingArchive}
                style={{
                  ...getFieldStyle('leaves', styles.input),
                  ...(!isCreatingNewConfig ? { opacity: 0.75, cursor: 'not-allowed' } : {}),
                }}
              />
            </div>
          </div>

          <p
            style={{
              color:
                theme === "dark"
                  ? "rgba(255,255,255,0.75)"
                  : "rgba(15,23,42,0.75)",
              marginBottom: "20px",
              fontSize: "clamp(14px, 2vw, 15px)",
              textAlign: isRTL ? "right" : "left",
            }}
          >
            {t.leaves.progressiveLeave}
          </p>

          {/* Early Request Threshold Section */}
          <div style={styles.contentSection}>
            <h3 style={styles.sectionTitle}>
              <AlertCircle size={26} />
              {t.attendance.halfDayRequestThreshold}
            </h3>

            <p style={{
              color: theme === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(15,23,42,0.7)',
              fontSize: '13px',
              marginBottom: '16px',
              lineHeight: '1.6',
            }}>
              {t.attendance.halfDayRequestThresholdDesc}
            </p>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  {t.attendance.halfDayRequestThreshold}
                </label>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                }}>
                  <PermissionInput
                    section="attendance"
                    type="number"
                    min="0.5"
                    max="24"
                    step="0.5"
                    value={getAttendanceValue("halfDayRequestThreshold") ?? ""}
                    onChange={(e) => {
                      const val = e.target.value === "" ? null : parseFloat(e.target.value);
                      if (val === null || (!isNaN(val) && val >= 0.5 && val <= 24)) {
                        handleAttendanceChange("halfDayRequestThreshold", val);
                      }
                    }}
                    style={{ ...getFieldStyle('attendance', styles.input), flex: 1 }}
                    placeholder="اختياري / Optional"
                  />
                  <span style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: theme === 'dark' ? 'rgba(255,255,255,0.8)' : 'rgba(15,23,42,0.8)',
                    minWidth: '60px',
                    textAlign: 'center',
                  }}>
                    {isRTL ? 'ساعات' : 'hours'}
                  </span>
                </div>
                <p style={{
                  fontSize: '12px',
                  color: theme === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.5)',
                  marginTop: '8px',
                  fontStyle: 'italic',
                }}>
                  {isRTL
                    ? 'القيمة الحالية: ' + (getAttendanceValue("halfDayRequestThreshold") || 'لم يتم التعيين') + ' ' + (getAttendanceValue("halfDayRequestThreshold") ? 'ساعات' : '')
                    : 'Current value: ' + (getAttendanceValue("halfDayRequestThreshold") || 'Not set') + ' ' + (getAttendanceValue("halfDayRequestThreshold") ? 'hours' : '')}
                </p>
              </div>
            </div>
          </div>

          {config.annualLeaveRules.map((rule, index) => (
            <div key={index} style={styles.leaveRuleContainer}>
              <div style={styles.formGroup}>
                <label style={styles.label}>{t.leaves.fromYear}</label>
                <input
                  type="text"
                  min="0"
                  value={rule.yearsFrom}
                  disabled={!canEdit}
                  onChange={(e) => {
                    if (canEdit) {
                      const newRules = [...config.annualLeaveRules];
                      newRules[index].yearsFrom = parseInt(e.target.value);
                      setConfig({ ...config, annualLeaveRules: newRules });
                    }
                  }}
                  style={getFieldStyle('leaves', styles.input)}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>{t.leaves.toYear}</label>
                <input
                  type="text"
                  min="0"
                  value={rule.yearsTo === null ? "" : rule.yearsTo}
                  onChange={(e) => {
                    if (!canEdit) return;
                    const newRules = [...config.annualLeaveRules];
                    newRules[index].yearsTo =
                      e.target.value === "" ? null : parseInt(e.target.value);
                    setConfig({ ...config, annualLeaveRules: newRules });
                  }}
                  disabled={!canEdit}
                  placeholder={t.leaves.unlimited}
                  style={getFieldStyle('leaves', styles.input)}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>{t.leaves.annualLeaveDays}</label>
                <input
                  type="text"
                  min="0"
                  value={rule.days}
                  onChange={(e) => {
                    if (!canEdit) return;
                    const newRules = [...config.annualLeaveRules];
                    newRules[index].days = parseInt(e.target.value);
                    setConfig({ ...config, annualLeaveRules: newRules });
                  }}
                  disabled={!canEdit}
                  style={getFieldStyle('leaves', styles.input)}
                />
              </div>
            </div>
          ))}

          <button
            onClick={() => {
              if (!canEdit) return;
              const newRule = { yearsFrom: 10, yearsTo: null, days: 30 };
              setConfig({
                ...config,
                annualLeaveRules: [...config.annualLeaveRules, newRule],
              });
            }}
            disabled={!canEdit}
            style={{ ...styles.addButton, opacity: canEdit ? 1 : 0.6, cursor: canEdit ? 'pointer' : 'not-allowed' }}
          >
            <Plus size={18} />
            {t.leaves.addRule}
          </button>

          {config.annualLeaveRules.length > 1 && (
            <button
              onClick={() => {
                if (!canEdit) return;
                setConfig({
                  ...config,
                  annualLeaveRules: config.annualLeaveRules.slice(0, -1),
                });
              }}
              disabled={!canEdit}
              style={{ ...styles.removeButton, marginTop: "14px", opacity: canEdit ? 1 : 0.6, cursor: canEdit ? 'pointer' : 'not-allowed' }}
            >
              <X size={16} />
              {t.leaves.removeRule}
            </button>
          )}
        </div>

        <div style={styles.contentSection}>
          <h3 style={styles.sectionTitle}>
            <FileText size={26} />
            {t.leaves.otherLeaveTypes}
          </h3>
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>{t.leaves.sickLeavePerYear}</label>
              <input
                type="text"
                min="0"
                value={config.sickLeavePerYear}
                onChange={(e) =>
                  canEdit && setConfig({
                    ...config,
                    sickLeavePerYear: parseInt(e.target.value),
                  })
                }
                disabled={!canEdit}
                style={getFieldStyle('leaves', styles.input)}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>{t.leaves.casualLeavePerYear}</label>
              <input
                type="text"
                min="0"
                value={config.casualLeavePerYear}
                onChange={(e) =>
                  canEdit && setConfig({
                    ...config,
                    casualLeavePerYear: parseInt(e.target.value),
                  })
                }
                disabled={!canEdit}
                style={getFieldStyle('leaves', styles.input)}
              />
            </div>
          </div>

          <div style={styles.toggleContainer}>
            <label style={styles.label}>{t.leaves.leaveApprovalRequired}</label>
            <input
              type="checkbox"
              checked={config.leaveApprovalRequired}
              onChange={(e) =>
                canEdit && setConfig({ ...config, leaveApprovalRequired: e.target.checked })
              }
              disabled={!canEdit}
              style={{ ...styles.checkbox, cursor: canEdit ? 'pointer' : 'not-allowed', opacity: canEdit ? 1 : 0.6 }}
            />
          </div>

          <div style={styles.toggleContainer}>
            <label style={styles.label}>{t.leaves.carryOverLeaves}</label>
            <input
              type="checkbox"
              checked={config.carryOverLeaves}
              onChange={(e) =>
                canEdit && setConfig({ ...config, carryOverLeaves: e.target.checked })
              }
              disabled={!canEdit}
              style={{ ...styles.checkbox, cursor: canEdit ? 'pointer' : 'not-allowed', opacity: canEdit ? 1 : 0.6 }}
            />
          </div>
        </div>
        
      </>
    );
  };

  // ============ الرندر الرئيسي ============
  // ============================================================================
  // Payroll Dates Tab
  // ============================================================================
  const renderPayrollDatesTab = () => {
    const canEdit = canEditField('company');
    const days = Array.from({ length: 31 }, (_, i) => i + 1);

    return (
      <div style={styles.contentSection}>
        <h3 style={styles.sectionTitle}>
          <Calendar size={26} />
          {language === 'ar' ? 'إعدادات تاريخ الراتب' : 'Payroll Date Settings'}
          {isEditingArchive && (
            <span style={{ fontSize: '12px', fontWeight: 800, marginLeft: '12px', padding: '4px 12px', borderRadius: '6px', background: 'rgba(245,158,11,0.18)', color: '#d97706' }}>
              {language === 'ar' ? 'التاريخ' : 'HISTORY'}
            </span>
          )}
        </h3>

        <p style={{ color: theme === 'dark' ? 'rgba(255,255,255,0.65)' : 'rgba(15,23,42,0.65)', fontSize: '14px', marginBottom: '28px', lineHeight: '1.6' }}>
          {language === 'ar'
            ? 'حدد يوم بداية دورة الرواتب الشهرية.'
            : 'Define the start day of the monthly payroll cycle.'}
        </p>

        <div style={styles.formRow}>
          <div style={styles.formGroup}>
            <label style={styles.label}>
              {language === 'ar' ? 'يوم بداية الراتب' : 'Payroll Start Day'}
            </label>
            <select
              value={config.payrollStartDate ?? ''}
              onChange={(e) => canEdit && setConfig((prev) => ({ ...prev, payrollStartDate: e.target.value ? parseInt(e.target.value) : null }))}
              disabled={!canEdit}
              style={{
                ...styles.select,
                ...(!canEdit ? { opacity: 0.7, cursor: 'not-allowed' } : {}),
                ...(isEditingArchive ? { borderColor: '#f59e0b' } : {}),
              }}
            >
              <option value="">{language === 'ar' ? '-- اليوم --' : '-- Day --'}</option>
              {days.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Preview */}
        {config.payrollStartDate && (
          <div style={{
            marginTop: 8,
            padding: '14px 18px',
            borderRadius: 10,
            background: theme === 'dark' ? 'rgba(46,124,186,0.12)' : 'rgba(46,124,186,0.07)',
            border: '1px solid rgba(46,124,186,0.25)',
            fontSize: 14,
            color: theme === 'dark' ? '#93c5fd' : '#1e40af',
            fontWeight: 600,
          }}>
            {language === 'ar'
              ? `دورة الراتب تبدأ من يوم ${config.payrollStartDate}`
              : `Payroll cycle starts on day ${config.payrollStartDate}`}
          </div>
        )}
      </div>
    );
  };

  // ============ الرندر الرئيسي ============
  return (
    <div style={styles.container}>
      <div style={styles.contentSection}>
        <div style={styles.headerGradientOverlay}></div>
        <div style={styles.headerContent}>
          <div style={styles.headerTop}>
            <div style={styles.headerLeft}>
              {/* العنوان والشركة الحالية */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                  flexWrap: "wrap",
                  marginBottom: "8px",
                }}
              >
                <h1 style={styles.title}>
                  {!isRTL && <Settings size={36} />}
                  {t.pageTitle}
                  {isRTL && <Settings size={36} />}
                </h1>
              </div>

              {/* الوصف */}
              <p style={styles.subtitle}>
                {t.pageSubtitle}
                <br></br>
                <br></br>

                {selectedCompanyName && (
                  <div style={styles.companyBadge}>
                    {!isRTL && <Building size={18} />}
                    {t.currentlyConfiguring} {selectedCompanyName}
                    {isRTL && <Building size={18} />}
                  </div>
                )}
              </p>
            </div>

            {/* زر اختيار الشركة */}
            <div style={styles.headerRight}>
              {modulePermissions && modulePermissions.detectCompanies && (
                <button
                  onClick={() => handleDetectCompanies()}
                  disabled={detectingCompanies}
                  style={styles.detectButton}
                >
                  {!isRTL && <Search size={20} />}
                  {detectingCompanies ? t.detectingCompanies : t.detectCompanies}
                  {isRTL && <Search size={20} />}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showCompanyModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <button
              onClick={() => setShowCompanyModal(false)}
              style={styles.closeButton}
              aria-label="Close modal"
            >
              ×
            </button>

            <h2 style={styles.modalTitle}>
              {t.companyModal.title}
              {!isRTL && <Building size={32} />}
              {isRTL && <Building size={32} />}
            </h2>
            <p style={styles.modalSubtitle}>{t.companyModal.subtitle}</p>

            {detectedCompanies.length === 0 ? (
              <p
                style={{
                  color:
                    theme === "dark"
                      ? "rgba(255,255,255,0.7)"
                      : "rgba(15,23,42,0.7)",
                  textAlign: isRTL ? "right" : "left",
                  fontSize: "clamp(14px, 2vw, 16px)",
                }}
              >
                {t.companyModal.noCompanies}
              </p>
            ) : (
              <div>
                {detectedCompanies.map((company, index) => (
                  <div
                    key={index}
                    style={styles.companyCard(company.isConfigured)}
                  // onMouseEnter={(e) => {
                  //   e.currentTarget.style.transform = 'translateY(-4px)';
                  //   e.currentTarget.style.boxShadow = theme === 'dark'
                  //     ? '0 12px 35px rgba(0,0,0,0.5)'
                  //     : '0 8px 28px rgba(0,0,0,0.12)';
                  // }}
                  // onMouseLeave={(e) => {
                  //   e.currentTarget.style.transform = 'translateY(0)';
                  //   e.currentTarget.style.boxShadow = 'none';
                  // }}
                  >
                    <div style={styles.companyName}>
                      {company.companyName}
                      {!isRTL && <Building size={22} />}
                      {isRTL && <Building size={22} />}
                    </div>
                    <div style={styles.companyDetails}>
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          flexDirection: isRTL ? "row" : "row",
                        }}
                      >
                        <Users size={16} />
                        {company.employeeCount || 0}{" "}
                        {t.companyModal.employeeCount}
                      </span>
                      <div style={styles.badge(company.isConfigured)}>
                        {company.isConfigured
                          ? t.companyModal.configured
                          : t.companyModal.notConfigured}
                      </div>
                    </div>
                    <button
                      onClick={() => handleSelectCompany(company.companyName)}
                      style={styles.selectCompanyButton}
                    // onMouseEnter={(e) => {
                    //   e.target.style.transform = 'translateY(-2px)';
                    //   e.target.style.boxShadow = '0 8px 22px rgba(46, 124, 186, 0.45)';
                    // }}
                    // onMouseLeave={(e) => {
                    //   e.target.style.transform = 'translateY(0)';
                    //   e.target.style.boxShadow = '0 5px 15px rgba(46, 124, 186, 0.35)';
                    // }}
                    >
                      {t.companyModal.selectCompany}
                    </button>
                  </div>
                ))}
              </div>
            )}

            <button
              onClick={() => handleDetectCompanies(true)}
              style={{ ...styles.autoCreateButton, marginTop: "24px" }}
              disabled={detectingCompanies}
            // onMouseEnter={(e) => {
            //   if (!detectingCompanies) {
            //     e.target.style.transform = 'translateY(-2px)';
            //     e.target.style.boxShadow = '0 8px 22px rgba(245, 158, 11, 0.45)';
            //   }
            // }}
            // onMouseLeave={(e) => {
            //   e.target.style.transform = 'translateY(0)';
            //   e.target.style.boxShadow = '0 5px 15px rgba(245, 158, 11, 0.35)';
            // }}
            >
              {t.companyModal.autoCreate}
            </button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={styles.contentSection}>
        <div style={styles.tabsContainer}>
          {[
            { id: "company", label: t.tabs.companyInfo, icon: Building, permission: "viewCompanyInfo" },
            { id: "attendance", label: t.tabs.attendance, icon: Clock, permission: "viewAttendance" },
            { id: "overtime", label: t.tabs.overtime, icon: TrendingUp, permission: "viewOvertime" },
            { id: "leaves", label: t.tabs.leaves, icon: Calendar, permission: "viewLeaves" },
            {
              id: "advancements",
              label: t.tabs.advancements,
              icon: DollarSign,
              permission: "viewAdvancements",
            },
            { id: "payrollDates", label: t.tabs.payrollDates, icon: Calendar, permission: "viewCompanyInfo" },
          ].filter(tab => modulePermissions[tab.permission]).map((tab) => {
            const IconComponent = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={styles.tab(activeTab === tab.id)}
              // onMouseEnter={(e) => {
              //   if (activeTab !== tab.id) {
              //     e.target.style.background = theme === 'dark'
              //       ? 'rgba(46, 124, 186, 0.15)'
              //       : 'rgba(46, 124, 186, 0.08)';
              //   }
              // }}
              // onMouseLeave={(e) => {
              //   if (activeTab !== tab.id) {
              //     e.target.style.background = 'transparent';
              //   }
              // }}
              >
                {/* {isRTL && <IconComponent size={20} />} */}
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content — wrapped with amber frame when in archive edit mode */}
      <div style={{
        ...styles.contentSection,
        border: isEditingArchive ? '2px solid #f59e0b' : styles.contentSection.border,
        boxShadow: isEditingArchive
          ? (theme === 'dark'
            ? '0 0 0 4px rgba(245,158,11,0.18), 0 8px 24px rgba(0,0,0,0.3)'
            : '0 0 0 4px rgba(245,158,11,0.12), 0 4px 16px rgba(0,0,0,0.06)')
          : styles.contentSection.boxShadow,
        background: isEditingArchive
          ? (theme === 'dark' ? 'rgba(245,158,11,0.04)' : 'rgba(255,251,235,0.5)')
          : styles.contentSection.background,
      }}>
        {activeTab === "company" && renderCompanyTab()}
        {activeTab === "attendance" && renderAttendanceTab()}
        {activeTab === "overtime" && renderOvertimeTab()}
        {activeTab === "leaves" && renderLeavesTab()}
        {activeTab === "advancements" && renderAdvancementsTab()}
        {activeTab === "payrollDates" && renderPayrollDatesTab()}
      </div>
      {/* Action Buttons */}
      <div style={styles.contentSection}>
        <div
          style={{
            marginBottom: 12,
            padding: "10px 12px",
            borderRadius: 8,
            fontSize: 12,
            fontWeight: 600,
            border: isCreatingNewConfig
              ? "1px solid rgba(59,130,246,0.4)"
              : "1px solid rgba(148,163,184,0.35)",
            background: isCreatingNewConfig
              ? (theme === "dark" ? "rgba(59,130,246,0.14)" : "rgba(219,234,254,0.8)")
              : (theme === "dark" ? "rgba(148,163,184,0.12)" : "rgba(241,245,249,0.9)"),
            color: isCreatingNewConfig
              ? (theme === "dark" ? "#93c5fd" : "#1d4ed8")
              : (theme === "dark" ? "rgba(226,232,240,0.9)" : "#475569"),
          }}
        >
          {isCreatingNewConfig
            ? t.effectiveDateBannerCreate
            : t.effectiveDateBannerDefault}
        </div>
        <div style={styles.actionButtons}>
          {modulePermissions && modulePermissions.reset && (
            <button
              onClick={handleReset}
              disabled={loading}
              style={styles.resetButton}
            // onMouseEnter={(e) => {
            //   if (!loading) {
            //     e.target.style.transform = 'translateY(-2px)';
            //     e.target.style.boxShadow = theme === 'dark'
            //       ? '0 8px 22px rgba(255,255,255,0.15)'
            //       : '0 6px 18px rgba(0,0,0,0.12)';
            //   }
            // }}
            // onMouseLeave={(e) => {
            //   e.target.style.transform = 'translateY(0)';
            //   e.target.style.boxShadow = 'none';
            // }}
            >
              {t.resetToDefault}
              {!isRTL && <RotateCcw size={20} />}
              {isRTL && <RotateCcw size={20} />}
            </button>
          )}
          {modulePermissions && modulePermissions.edit && (
            <button
              onClick={handleCreateConfig}
              disabled={loading || isEditingArchive}
              style={{
                ...styles.saveButton,
                background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
                boxShadow: "0 10px 25px rgba(37, 99, 235, 0.35)",
                opacity: isEditingArchive ? 0.6 : 1,
                cursor: isEditingArchive ? "not-allowed" : "pointer",
              }}
              title={
                isEditingArchive
                  ? (isRTL ? "اخرج من وضع الأرشيف أولاً" : "Exit archive mode first")
                  : undefined
              }
            >
              {loading
                ? t.creatingConfiguration
                : isCreatingNewConfig
                  ? t.confirmCreateConfiguration
                  : t.startCreateConfiguration}
              {!isRTL && <Plus size={20} />}
              {isRTL && <Plus size={20} />}
            </button>
          )}
          {modulePermissions && modulePermissions.edit && isCreatingNewConfig && (
            <button
              onClick={() => setIsCreatingNewConfig(false)}
              disabled={loading}
              style={styles.resetButton}
            >
              {t.cancelCreateConfiguration}
              {!isRTL && <X size={20} />}
              {isRTL && <X size={20} />}
            </button>
          )}
          {modulePermissions && modulePermissions.edit && (
            <button
              onClick={handleSave}
              disabled={loading || isCreatingNewConfig}
              style={styles.saveButton}
            // onMouseEnter={(e) => {
            //   if (!loading) {
            //     e.target.style.transform = 'translateY(-2px)';
            //     e.target.style.boxShadow = '0 12px 30px rgba(16, 185, 129, 0.45)';
            //   }
            // }}
            // onMouseLeave={(e) => {
            //   e.target.style.transform = 'translateY(0)';
            //   e.target.style.boxShadow = '0 10px 25px rgba(16, 185, 129, 0.35)';
            // }}
            >
              {loading ? t.saving : t.saveConfiguration}
              {!isRTL && <Save size={20} />}
              {isRTL && <Save size={20} />}
            </button>
          )}
        </div>

        {/* Success Notification */}
        {saveSuccess && (
          <div style={styles.successNotification}>
            {t.saveSuccess}
            {!isRTL && <CheckCircle size={22} />}
            {isRTL && <CheckCircle size={22} />}
          </div>
        )}
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(30px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes slideInUp {
          from { transform: translateY(120px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
          .hover-stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
  border-color: #3b82f6;
}
        
        input:hover, select:hover { 
          border-color: #2e7cba !important;
          transform: translateY(-1px);
        }
        input:focus, select:focus { 
          outline: none;
          border-color: #2e7cba !important;
          box-shadow: 0 0 0 4px rgba(46, 124, 186, 0.15) !important;
        }
        button:hover:not(:disabled) {
          transform: translateY(-2px);
        }
        button:active:not(:disabled) {
          transform: translateY(0);
        }
        button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        
        /* Scrollbar Styling */
        ::-webkit-scrollbar {
          width: 10px;
          height: 10px;
        }
        ::-webkit-scrollbar-track {
          background: ${theme === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"
        };
          border-radius: 10px;
        }
        ::-webkit-scrollbar-thumb {
          background: ${theme === "dark"
          ? "rgba(46, 124, 186, 0.4)"
          : "rgba(46, 124, 186, 0.3)"
        };
          border-radius: 10px;
        }
        ::-webkit-scrollbar-thumb:hover {
          background: ${theme === "dark"
          ? "rgba(46, 124, 186, 0.6)"
          : "rgba(46, 124, 186, 0.5)"
        };
        }
        
        @media (max-width: 768px) {
          input, select, button { 
            font-size: 16px !important; 
          }
        }
      `}</style>

      {/* ===== ALERT MODAL ===== */}
      <AlertModal
        open={alertModal.open}
        title={alertModal.title}
        message={alertModal.message}
        variant={alertModal.variant}
        onClose={closeAlert}
        theme={theme}
        language={language}
      />
    </div>
  );
};
export default CompanyConfiguration;
